import * as z from 'zod';

export const CreateContactRequest = z.object({
  info: z
    .object({
      name: z
        .object({
          first: z
            .string()
            .describe("Contact's first name.")
            .max(1000)
            .optional()
            .nullable(),
          last: z
            .string()
            .describe("Contact's last name.")
            .max(1000)
            .optional()
            .nullable(),
        })
        .describe("Contact's first and last name.")
        .optional(),
      emails: z
        .object({
          items: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Email ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                tag: z.enum(['UNTAGGED', 'MAIN', 'HOME', 'WORK']).optional(),
                email: z
                  .string()
                  .describe('Email address.')
                  .min(1)
                  .max(320)
                  .optional(),
                primary: z
                  .boolean()
                  .describe(
                    "Indicates whether this is the contact's primary email address.\nWhen changing `primary` to `true` for an email,\nthe contact's other emails become `false`.\nChanging the primary email of a contact also affects the subscription status to marketing emails that are decided based on the primary email."
                  )
                  .optional()
                  .nullable(),
              })
            )
            .max(50)
            .optional(),
        })
        .describe("Contact's email addresses.")
        .optional(),
      phones: z
        .object({
          items: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Phone ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                tag: z
                  .enum(['UNTAGGED', 'MAIN', 'HOME', 'MOBILE', 'WORK', 'FAX'])
                  .optional(),
                countryCode: z
                  .string()
                  .describe(
                    '[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) country code.'
                  )
                  .optional()
                  .nullable(),
                phone: z
                  .string()
                  .describe('Phone number.')
                  .min(1)
                  .max(50)
                  .optional(),
                e164Phone: z
                  .string()
                  .describe(
                    '[ITU E.164-formatted](https://www.itu.int/rec/T-REC-E.164/)\nphone number.\nAutomatically generated using `phone` and `countryCode`, pending both values are valid.'
                  )
                  .optional()
                  .nullable(),
                primary: z
                  .boolean()
                  .describe(
                    "Indicates whether this is the contact's primary phone number.\nWhen changing `primary` to `true` for a phone,\nthe contact's `primary` field for other phones becomes `false`.\nChanging the primary phone number also affects the subscription status to SMS messages that are decided based on the primary phone."
                  )
                  .optional()
                  .nullable(),
              })
            )
            .max(50)
            .optional(),
        })
        .describe("Contact's phone numbers.")
        .optional(),
      addresses: z
        .object({
          items: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Street address ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                tag: z
                  .enum(['UNTAGGED', 'HOME', 'WORK', 'BILLING', 'SHIPPING'])
                  .optional(),
                address: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                    addressLine2: z.string().optional().nullable(),
                  })
                  .describe('Street address.')
                  .optional(),
              })
            )
            .max(50)
            .optional(),
        })
        .describe("Contact's street addresses.")
        .optional(),
      company: z
        .string()
        .describe("Contact's company name.")
        .max(1000)
        .optional()
        .nullable(),
      jobTitle: z
        .string()
        .describe("Contact's job title.")
        .max(1000)
        .optional()
        .nullable(),
      birthdate: z
        .string()
        .describe(
          'Birth date in `YYYY-MM-DD` format. For example, `2020-03-15`.'
        )
        .optional()
        .nullable(),
      locale: z
        .string()
        .describe(
          'Locale in\n[IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nTypically, this is a lowercase 2-letter language code,\nfollowed by a hyphen, followed by an uppercase 2-letter country code.\nFor example, `en-US` for U.S. English, and `de-DE` for Germany German.'
        )
        .optional()
        .nullable(),
      labelKeys: z
        .object({ items: z.array(z.string()).max(2000).optional() })
        .describe(
          "List of contact's labels.\n\nLabels are used to organize contacts. Labels can be\nadded and removed using Label Contact and Unlabel Contact, respectively.\n\nTo view or manage contact labels, use the Labels API."
        )
        .optional(),
      extendedFields: z
        .object({
          items: z
            .record(z.string(), z.any())
            .describe(
              "Contact's extended fields,\nwhere each key is the field key,\nand each value is the field's value for the contact.\n\nTo view and manage extended fields, use the Extended Fields API."
            )
            .optional(),
        })
        .describe('Additional custom fields.\n\nEmpty fields are not returned.')
        .optional(),
      picture: z
        .object({
          image: z
            .string()
            .describe(
              'Image.\nThis can contain an image URL or a Wix Media image ID.'
            )
            .optional(),
        })
        .describe("Contact's profile picture.")
        .optional(),
    })
    .describe('Contact info.'),
  options: z
    .object({
      allowDuplicates: z
        .boolean()
        .describe(
          'Controls whether the call will succeed\nif the new contact information contains an email or a phone number already used by another contact.\n\nIf set to `true`,\nthe call will succeed even if an email address or phone number is used by another contact.\nIf set to `false`,\nthe call will fail if the given email address is used by another contact or,\nif the email address is not given and the given phone number is used by another contact.\n\nDefault: `false`'
        )
        .optional(),
    })
    .describe('Create contact options.')
    .optional(),
});
export const CreateContactResponse = z.object({
  contact: z
    .object({
      _id: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      revision: z
        .number()
        .int()
        .describe(
          'Revision number, which increments by 1 each time the contact is updated.\nTo prevent conflicting changes,\nthe existing `revision` must be specified when updating a contact.'
        )
        .optional(),
      source: z
        .object({
          sourceType: z
            .enum([
              'OTHER',
              'ADMIN',
              'WIX_APP',
              'IMPORT',
              'THIRD_PARTY',
              'WIX_BOOKINGS',
              'WIX_CHAT',
              'WIX_EMAIL_MARKETING',
              'WIX_EVENTS',
              'WIX_FORMS',
              'WIX_GROUPS',
              'WIX_HOTELS',
              'WIX_MARKET_PLACE',
              'WIX_MUSIC',
              'WIX_RESTAURANTS',
              'WIX_SITE_MEMBERS',
              'WIX_STORES',
              'WIX_CODE',
              'HOPP',
            ])
            .describe('Source type.')
            .optional(),
          appId: z
            .string()
            .describe('App ID, if the contact was created by an app.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe("Details about the contact's source.")
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the contact was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the contact was last updated.')
        .optional()
        .nullable(),
      lastActivity: z
        .object({
          activityDate: z
            .date()
            .describe('Date and time of the last action.')
            .optional()
            .nullable(),
          activityType: z
            .enum([
              'GENERAL',
              'CONTACT_CREATED',
              'MEMBER_LOGIN',
              'MEMBER_REGISTER',
              'MEMBER_STATUS_CHANGED',
              'FORM_SUBMITTED',
              'INBOX_FORM_SUBMITTED',
              'INBOX_PAYMENT_REQUEST_PAID',
              'INBOX_MESSAGE_TO_CUSTOMER',
              'INBOX_MESSAGE_FROM_CUSTOMER',
              'NEWSLETTER_SUBSCRIPTION_FORM_SUBMITTED',
              'NEWSLETTER_SUBSCRIPTION_UNSUBSCRIBE',
              'ECOM_PURCHASE',
              'ECOM_CART_ABANDON',
              'ECOM_CHECKOUT_BUYER',
              'BOOKINGS_APPOINTMENT',
              'HOTELS_RESERVATION',
              'HOTELS_PURCHASE',
              'HOTELS_CONFIRMATION',
              'HOTELS_CANCEL',
              'VIDEO_PURCHASE',
              'VIDEO_RENT',
              'CASHIER_BUTTON_PURCHASE',
              'ARENA_NEW_LEAD',
              'EVENTS_RSVP',
              'INVOICE_PAY',
              'INVOICE_OVERDUE',
              'PRICE_QUOTE_ACCEPT',
              'PRICE_QUOTE_EXPIRE',
              'RESTAURANTS_ORDER',
              'RESTAURANTS_RESERVATION',
              'SHOUTOUT_OPEN',
              'SHOUTOUT_CLICK',
              'CONTACT_MERGED',
              'NEWSLETTER_SUBSCRIPTION_SUBSCRIBE',
              'NEWSLETTER_SUBSCRIPTION_PENDING',
              'NEWSLETTER_SUBSCRIPTION_NOT_SET',
              'PHONE_SUBSCRIPTION_SUBSCRIBE',
              'PHONE_SUBSCRIPTION_PENDING',
              'PHONE_SUBSCRIPTION_NOT_SET',
              'PHONE_SUBSCRIPTION_UNSUBSCRIBE',
            ])
            .describe("Contact's last action in the site.")
            .optional(),
        })
        .describe("Details about the contact's last action in the site.")
        .optional(),
      primaryInfo: z
        .object({
          email: z
            .string()
            .describe(
              'Primary email address.\n\nThis property reflects the email address in `info.emails`\nwhere `primary` is `true`.'
            )
            .email()
            .optional()
            .nullable(),
          phone: z
            .string()
            .describe(
              'Primary phone number.\n\nThis property reflects the phone number in `info.phones`\nwhere `primary` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe("Contact's primary phone and email.")
        .optional(),
      info: z
        .object({
          name: z
            .object({
              first: z
                .string()
                .describe("Contact's first name.")
                .max(1000)
                .optional()
                .nullable(),
              last: z
                .string()
                .describe("Contact's last name.")
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe("Contact's first and last name.")
            .optional(),
          emails: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Email ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum(['UNTAGGED', 'MAIN', 'HOME', 'WORK'])
                      .describe(
                        'Email type.\n\n`UNTAGGED` is shown as "Other" in the Contact List.'
                      )
                      .optional(),
                    email: z
                      .string()
                      .describe('Email address.')
                      .min(1)
                      .max(320)
                      .optional(),
                    primary: z
                      .boolean()
                      .describe(
                        "Indicates whether this is the contact's primary email address.\nWhen changing `primary` to `true` for an email,\nthe contact's other emails become `false`.\nChanging the primary email of a contact also affects the subscription status to marketing emails that are decided based on the primary email."
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's email addresses.")
            .optional(),
          phones: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Phone ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum([
                        'UNTAGGED',
                        'MAIN',
                        'HOME',
                        'MOBILE',
                        'WORK',
                        'FAX',
                      ])
                      .describe(
                        'Phone type.\n\n`UNTAGGED` is shown as "Other" in the Contact List.'
                      )
                      .optional(),
                    countryCode: z
                      .string()
                      .describe(
                        '[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) country code.'
                      )
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Phone number.')
                      .min(1)
                      .max(50)
                      .optional(),
                    e164Phone: z
                      .string()
                      .describe(
                        '[ITU E.164-formatted](https://www.itu.int/rec/T-REC-E.164/)\nphone number.\nAutomatically generated using `phone` and `countryCode`, pending both values are valid.'
                      )
                      .optional()
                      .nullable(),
                    primary: z
                      .boolean()
                      .describe(
                        "Indicates whether this is the contact's primary phone number.\nWhen changing `primary` to `true` for a phone,\nthe contact's `primary` field for other phones becomes `false`.\nChanging the primary phone number also affects the subscription status to SMS messages that are decided based on the primary phone."
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's phone numbers.")
            .optional(),
          addresses: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Street address ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum(['UNTAGGED', 'HOME', 'WORK', 'BILLING', 'SHIPPING'])
                      .describe(
                        'Address type.\n`UNTAGGED` is shown as "Other" in the Contact List.'
                      )
                      .optional(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                        addressLine2: z.string().optional().nullable(),
                      })
                      .describe('Street address.')
                      .optional(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's street addresses.")
            .optional(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(1000)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(1000)
            .optional()
            .nullable(),
          birthdate: z
            .string()
            .describe(
              'Birth date in `YYYY-MM-DD` format. For example, `2020-03-15`.'
            )
            .optional()
            .nullable(),
          locale: z
            .string()
            .describe(
              'Locale in\n[IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nTypically, this is a lowercase 2-letter language code,\nfollowed by a hyphen, followed by an uppercase 2-letter country code.\nFor example, `en-US` for U.S. English, and `de-DE` for Germany German.'
            )
            .optional()
            .nullable(),
          labelKeys: z
            .object({ items: z.array(z.string()).max(2000).optional() })
            .describe(
              "List of contact's labels.\n\nLabels are used to organize contacts. Labels can be\nadded and removed using Label Contact and Unlabel Contact, respectively.\n\nTo view or manage contact labels, use the Labels API."
            )
            .optional(),
          extendedFields: z
            .object({
              items: z
                .record(z.string(), z.any())
                .describe(
                  "Contact's extended fields,\nwhere each key is the field key,\nand each value is the field's value for the contact.\n\nTo view and manage extended fields, use the Extended Fields API."
                )
                .optional(),
            })
            .describe(
              'Additional custom fields.\n\nEmpty fields are not returned.'
            )
            .optional(),
          picture: z
            .object({
              image: z
                .string()
                .describe(
                  'Image.\nThis can contain an image URL or a Wix Media image ID.'
                )
                .optional(),
            })
            .describe("Contact's profile picture.")
            .optional(),
        })
        .describe("Contact's details.")
        .optional(),
      primaryEmail: z
        .object({
          email: z
            .string()
            .describe(
              'Primary email address.\n\nThis field reflects the email address in `info.emails`\nwhere `primary` is `true`.'
            )
            .email()
            .optional()
            .nullable(),
          subscriptionStatus: z
            .enum([
              'NO_SUBSCRIPTION_STATUS',
              'NOT_SET',
              'PENDING',
              'SUBSCRIBED',
              'UNSUBSCRIBED',
            ])
            .describe(
              "Indicates the recipient's opt-in or opt-out status for marketing emails.\n\nDefault: `NOT_SET`"
            )
            .optional(),
          deliverabilityStatus: z
            .enum(['NOT_SET', 'VALID', 'BOUNCED', 'SPAM_COMPLAINT', 'INACTIVE'])
            .describe(
              'Indicates last reported status of sent emails.\n\nDefault: `NOT_SET`'
            )
            .optional(),
        })
        .describe("Contact's primary email details.")
        .optional(),
      primaryPhone: z
        .object({
          countryCode: z
            .string()
            .describe(
              'Country code of the primary phone number in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
            )
            .optional()
            .nullable(),
          e164Phone: z
            .string()
            .describe(
              'Phone number in [ITU E.164](https://www.itu.int/rec/T-REC-E.164/) format.\nAutomatically generated using `phone` and `countryCode` when both values are valid.'
            )
            .optional()
            .nullable(),
          formattedPhone: z
            .string()
            .describe(
              'Formatted phone. Automatically generated using `phone` and `countryCode`.'
            )
            .optional()
            .nullable(),
          subscriptionStatus: z
            .enum([
              'NO_SUBSCRIPTION_STATUS',
              'NOT_SET',
              'PENDING',
              'SUBSCRIBED',
              'UNSUBSCRIBED',
            ])
            .describe(
              "Indicates the recipient's opt-in or opt-out status for SMS messages.\n\nDefault:\n- `NOT_SET` when the phone is a valid E164 phone.\n- `NO_SUBSCRIPTION_STATUS` when the phone is not a valid E164 phone."
            )
            .optional(),
          phone: z
            .string()
            .describe(
              'Primary phone number.\n\nThis field reflects the phone number in `info.phones`\nwhere `primary` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe("Contact's primary phone details.")
        .optional(),
    })
    .describe('Contact.')
    .optional(),
});
export const UpdateContactRequest = z.object({
  contactId: z
    .string()
    .describe('ID of the contact to update.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  info: z
    .object({
      name: z
        .object({
          first: z
            .string()
            .describe("Contact's first name.")
            .max(1000)
            .optional()
            .nullable(),
          last: z
            .string()
            .describe("Contact's last name.")
            .max(1000)
            .optional()
            .nullable(),
        })
        .describe("Contact's first and last name.")
        .optional(),
      emails: z
        .object({
          items: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Email ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                tag: z.enum(['UNTAGGED', 'MAIN', 'HOME', 'WORK']).optional(),
                email: z
                  .string()
                  .describe('Email address.')
                  .min(1)
                  .max(320)
                  .optional(),
                primary: z
                  .boolean()
                  .describe(
                    "Indicates whether this is the contact's primary email address.\nWhen changing `primary` to `true` for an email,\nthe contact's other emails become `false`.\nChanging the primary email of a contact also affects the subscription status to marketing emails that are decided based on the primary email."
                  )
                  .optional()
                  .nullable(),
              })
            )
            .max(50)
            .optional(),
        })
        .describe("Contact's email addresses.")
        .optional(),
      phones: z
        .object({
          items: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Phone ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                tag: z
                  .enum(['UNTAGGED', 'MAIN', 'HOME', 'MOBILE', 'WORK', 'FAX'])
                  .optional(),
                countryCode: z
                  .string()
                  .describe(
                    '[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) country code.'
                  )
                  .optional()
                  .nullable(),
                phone: z
                  .string()
                  .describe('Phone number.')
                  .min(1)
                  .max(50)
                  .optional(),
                e164Phone: z
                  .string()
                  .describe(
                    '[ITU E.164-formatted](https://www.itu.int/rec/T-REC-E.164/)\nphone number.\nAutomatically generated using `phone` and `countryCode`, pending both values are valid.'
                  )
                  .optional()
                  .nullable(),
                primary: z
                  .boolean()
                  .describe(
                    "Indicates whether this is the contact's primary phone number.\nWhen changing `primary` to `true` for a phone,\nthe contact's `primary` field for other phones becomes `false`.\nChanging the primary phone number also affects the subscription status to SMS messages that are decided based on the primary phone."
                  )
                  .optional()
                  .nullable(),
              })
            )
            .max(50)
            .optional(),
        })
        .describe("Contact's phone numbers.")
        .optional(),
      addresses: z
        .object({
          items: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Street address ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                tag: z
                  .enum(['UNTAGGED', 'HOME', 'WORK', 'BILLING', 'SHIPPING'])
                  .optional(),
                address: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                    addressLine2: z.string().optional().nullable(),
                  })
                  .describe('Street address.')
                  .optional(),
              })
            )
            .max(50)
            .optional(),
        })
        .describe("Contact's street addresses.")
        .optional(),
      company: z
        .string()
        .describe("Contact's company name.")
        .max(1000)
        .optional()
        .nullable(),
      jobTitle: z
        .string()
        .describe("Contact's job title.")
        .max(1000)
        .optional()
        .nullable(),
      birthdate: z
        .string()
        .describe(
          'Birth date in `YYYY-MM-DD` format. For example, `2020-03-15`.'
        )
        .optional()
        .nullable(),
      locale: z
        .string()
        .describe(
          'Locale in\n[IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nTypically, this is a lowercase 2-letter language code,\nfollowed by a hyphen, followed by an uppercase 2-letter country code.\nFor example, `en-US` for U.S. English, and `de-DE` for Germany German.'
        )
        .optional()
        .nullable(),
      labelKeys: z
        .object({ items: z.array(z.string()).max(2000).optional() })
        .describe(
          "List of contact's labels.\n\nLabels are used to organize contacts. Labels can be\nadded and removed using Label Contact and Unlabel Contact, respectively.\n\nTo view or manage contact labels, use the Labels API."
        )
        .optional(),
      extendedFields: z
        .object({
          items: z
            .record(z.string(), z.any())
            .describe(
              "Contact's extended fields,\nwhere each key is the field key,\nand each value is the field's value for the contact.\n\nTo view and manage extended fields, use the Extended Fields API."
            )
            .optional(),
        })
        .describe('Additional custom fields.\n\nEmpty fields are not returned.')
        .optional(),
      picture: z
        .object({
          image: z
            .string()
            .describe(
              'Image.\nThis can contain an image URL or a Wix Media image ID.'
            )
            .optional(),
        })
        .describe("Contact's profile picture.")
        .optional(),
    })
    .describe('Contact info.'),
  revision: z
    .number()
    .int()
    .describe(
      'Revision number.\nWhen updating, include the existing `revision`\nto prevent conflicting updates.'
    ),
  options: z
    .object({
      allowDuplicates: z
        .boolean()
        .describe(
          'Controls whether the call will succeed\nif the new contact information contains an email or a phone number already used by another contact.\n\nIf set to `true`,\nthe call will succeed even if an email address or phone number is used by another contact.\nIf set to `false`,\nthe call will fail if the given email address is used by another contact or,\nif the email address is not given and the given phone number is used by another contact.\n\nDefault: `false`'
        )
        .optional(),
    })
    .describe('Contact update options.')
    .optional(),
});
export const UpdateContactResponse = z.object({
  contact: z
    .object({
      _id: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      revision: z
        .number()
        .int()
        .describe(
          'Revision number, which increments by 1 each time the contact is updated.\nTo prevent conflicting changes,\nthe existing `revision` must be specified when updating a contact.'
        )
        .optional(),
      source: z
        .object({
          sourceType: z
            .enum([
              'OTHER',
              'ADMIN',
              'WIX_APP',
              'IMPORT',
              'THIRD_PARTY',
              'WIX_BOOKINGS',
              'WIX_CHAT',
              'WIX_EMAIL_MARKETING',
              'WIX_EVENTS',
              'WIX_FORMS',
              'WIX_GROUPS',
              'WIX_HOTELS',
              'WIX_MARKET_PLACE',
              'WIX_MUSIC',
              'WIX_RESTAURANTS',
              'WIX_SITE_MEMBERS',
              'WIX_STORES',
              'WIX_CODE',
              'HOPP',
            ])
            .describe('Source type.')
            .optional(),
          appId: z
            .string()
            .describe('App ID, if the contact was created by an app.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe("Details about the contact's source.")
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the contact was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the contact was last updated.')
        .optional()
        .nullable(),
      lastActivity: z
        .object({
          activityDate: z
            .date()
            .describe('Date and time of the last action.')
            .optional()
            .nullable(),
          activityType: z
            .enum([
              'GENERAL',
              'CONTACT_CREATED',
              'MEMBER_LOGIN',
              'MEMBER_REGISTER',
              'MEMBER_STATUS_CHANGED',
              'FORM_SUBMITTED',
              'INBOX_FORM_SUBMITTED',
              'INBOX_PAYMENT_REQUEST_PAID',
              'INBOX_MESSAGE_TO_CUSTOMER',
              'INBOX_MESSAGE_FROM_CUSTOMER',
              'NEWSLETTER_SUBSCRIPTION_FORM_SUBMITTED',
              'NEWSLETTER_SUBSCRIPTION_UNSUBSCRIBE',
              'ECOM_PURCHASE',
              'ECOM_CART_ABANDON',
              'ECOM_CHECKOUT_BUYER',
              'BOOKINGS_APPOINTMENT',
              'HOTELS_RESERVATION',
              'HOTELS_PURCHASE',
              'HOTELS_CONFIRMATION',
              'HOTELS_CANCEL',
              'VIDEO_PURCHASE',
              'VIDEO_RENT',
              'CASHIER_BUTTON_PURCHASE',
              'ARENA_NEW_LEAD',
              'EVENTS_RSVP',
              'INVOICE_PAY',
              'INVOICE_OVERDUE',
              'PRICE_QUOTE_ACCEPT',
              'PRICE_QUOTE_EXPIRE',
              'RESTAURANTS_ORDER',
              'RESTAURANTS_RESERVATION',
              'SHOUTOUT_OPEN',
              'SHOUTOUT_CLICK',
              'CONTACT_MERGED',
              'NEWSLETTER_SUBSCRIPTION_SUBSCRIBE',
              'NEWSLETTER_SUBSCRIPTION_PENDING',
              'NEWSLETTER_SUBSCRIPTION_NOT_SET',
              'PHONE_SUBSCRIPTION_SUBSCRIBE',
              'PHONE_SUBSCRIPTION_PENDING',
              'PHONE_SUBSCRIPTION_NOT_SET',
              'PHONE_SUBSCRIPTION_UNSUBSCRIBE',
            ])
            .describe("Contact's last action in the site.")
            .optional(),
        })
        .describe("Details about the contact's last action in the site.")
        .optional(),
      primaryInfo: z
        .object({
          email: z
            .string()
            .describe(
              'Primary email address.\n\nThis property reflects the email address in `info.emails`\nwhere `primary` is `true`.'
            )
            .email()
            .optional()
            .nullable(),
          phone: z
            .string()
            .describe(
              'Primary phone number.\n\nThis property reflects the phone number in `info.phones`\nwhere `primary` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe("Contact's primary phone and email.")
        .optional(),
      info: z
        .object({
          name: z
            .object({
              first: z
                .string()
                .describe("Contact's first name.")
                .max(1000)
                .optional()
                .nullable(),
              last: z
                .string()
                .describe("Contact's last name.")
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe("Contact's first and last name.")
            .optional(),
          emails: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Email ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum(['UNTAGGED', 'MAIN', 'HOME', 'WORK'])
                      .describe(
                        'Email type.\n\n`UNTAGGED` is shown as "Other" in the Contact List.'
                      )
                      .optional(),
                    email: z
                      .string()
                      .describe('Email address.')
                      .min(1)
                      .max(320)
                      .optional(),
                    primary: z
                      .boolean()
                      .describe(
                        "Indicates whether this is the contact's primary email address.\nWhen changing `primary` to `true` for an email,\nthe contact's other emails become `false`.\nChanging the primary email of a contact also affects the subscription status to marketing emails that are decided based on the primary email."
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's email addresses.")
            .optional(),
          phones: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Phone ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum([
                        'UNTAGGED',
                        'MAIN',
                        'HOME',
                        'MOBILE',
                        'WORK',
                        'FAX',
                      ])
                      .describe(
                        'Phone type.\n\n`UNTAGGED` is shown as "Other" in the Contact List.'
                      )
                      .optional(),
                    countryCode: z
                      .string()
                      .describe(
                        '[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) country code.'
                      )
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Phone number.')
                      .min(1)
                      .max(50)
                      .optional(),
                    e164Phone: z
                      .string()
                      .describe(
                        '[ITU E.164-formatted](https://www.itu.int/rec/T-REC-E.164/)\nphone number.\nAutomatically generated using `phone` and `countryCode`, pending both values are valid.'
                      )
                      .optional()
                      .nullable(),
                    primary: z
                      .boolean()
                      .describe(
                        "Indicates whether this is the contact's primary phone number.\nWhen changing `primary` to `true` for a phone,\nthe contact's `primary` field for other phones becomes `false`.\nChanging the primary phone number also affects the subscription status to SMS messages that are decided based on the primary phone."
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's phone numbers.")
            .optional(),
          addresses: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Street address ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum(['UNTAGGED', 'HOME', 'WORK', 'BILLING', 'SHIPPING'])
                      .describe(
                        'Address type.\n`UNTAGGED` is shown as "Other" in the Contact List.'
                      )
                      .optional(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                        addressLine2: z.string().optional().nullable(),
                      })
                      .describe('Street address.')
                      .optional(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's street addresses.")
            .optional(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(1000)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(1000)
            .optional()
            .nullable(),
          birthdate: z
            .string()
            .describe(
              'Birth date in `YYYY-MM-DD` format. For example, `2020-03-15`.'
            )
            .optional()
            .nullable(),
          locale: z
            .string()
            .describe(
              'Locale in\n[IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nTypically, this is a lowercase 2-letter language code,\nfollowed by a hyphen, followed by an uppercase 2-letter country code.\nFor example, `en-US` for U.S. English, and `de-DE` for Germany German.'
            )
            .optional()
            .nullable(),
          labelKeys: z
            .object({ items: z.array(z.string()).max(2000).optional() })
            .describe(
              "List of contact's labels.\n\nLabels are used to organize contacts. Labels can be\nadded and removed using Label Contact and Unlabel Contact, respectively.\n\nTo view or manage contact labels, use the Labels API."
            )
            .optional(),
          extendedFields: z
            .object({
              items: z
                .record(z.string(), z.any())
                .describe(
                  "Contact's extended fields,\nwhere each key is the field key,\nand each value is the field's value for the contact.\n\nTo view and manage extended fields, use the Extended Fields API."
                )
                .optional(),
            })
            .describe(
              'Additional custom fields.\n\nEmpty fields are not returned.'
            )
            .optional(),
          picture: z
            .object({
              image: z
                .string()
                .describe(
                  'Image.\nThis can contain an image URL or a Wix Media image ID.'
                )
                .optional(),
            })
            .describe("Contact's profile picture.")
            .optional(),
        })
        .describe("Contact's details.")
        .optional(),
      primaryEmail: z
        .object({
          email: z
            .string()
            .describe(
              'Primary email address.\n\nThis field reflects the email address in `info.emails`\nwhere `primary` is `true`.'
            )
            .email()
            .optional()
            .nullable(),
          subscriptionStatus: z
            .enum([
              'NO_SUBSCRIPTION_STATUS',
              'NOT_SET',
              'PENDING',
              'SUBSCRIBED',
              'UNSUBSCRIBED',
            ])
            .describe(
              "Indicates the recipient's opt-in or opt-out status for marketing emails.\n\nDefault: `NOT_SET`"
            )
            .optional(),
          deliverabilityStatus: z
            .enum(['NOT_SET', 'VALID', 'BOUNCED', 'SPAM_COMPLAINT', 'INACTIVE'])
            .describe(
              'Indicates last reported status of sent emails.\n\nDefault: `NOT_SET`'
            )
            .optional(),
        })
        .describe("Contact's primary email details.")
        .optional(),
      primaryPhone: z
        .object({
          countryCode: z
            .string()
            .describe(
              'Country code of the primary phone number in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
            )
            .optional()
            .nullable(),
          e164Phone: z
            .string()
            .describe(
              'Phone number in [ITU E.164](https://www.itu.int/rec/T-REC-E.164/) format.\nAutomatically generated using `phone` and `countryCode` when both values are valid.'
            )
            .optional()
            .nullable(),
          formattedPhone: z
            .string()
            .describe(
              'Formatted phone. Automatically generated using `phone` and `countryCode`.'
            )
            .optional()
            .nullable(),
          subscriptionStatus: z
            .enum([
              'NO_SUBSCRIPTION_STATUS',
              'NOT_SET',
              'PENDING',
              'SUBSCRIBED',
              'UNSUBSCRIBED',
            ])
            .describe(
              "Indicates the recipient's opt-in or opt-out status for SMS messages.\n\nDefault:\n- `NOT_SET` when the phone is a valid E164 phone.\n- `NO_SUBSCRIPTION_STATUS` when the phone is not a valid E164 phone."
            )
            .optional(),
          phone: z
            .string()
            .describe(
              'Primary phone number.\n\nThis field reflects the phone number in `info.phones`\nwhere `primary` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe("Contact's primary phone details.")
        .optional(),
    })
    .describe('Updated contact.')
    .optional(),
});
export const MergeContactsRequest = z.object({
  targetContactId: z
    .string()
    .describe('Target contact ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  targetContactRevision: z
    .object({ value: z.number().int().optional() })
    .describe(
      "Target contact revision number, which increments by 1 each time the contact is updated.\nTo prevent conflicting changes,\nthe target contact's current `revision` must be specified."
    ),
  options: z
    .object({ sourceContactIds: z.array(z.string()).min(1).max(5).optional() })
    .describe('Merge contacts options.')
    .optional(),
});
export const MergeContactsResponse = z.object({
  contact: z
    .object({
      _id: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      revision: z
        .number()
        .int()
        .describe(
          'Revision number, which increments by 1 each time the contact is updated.\nTo prevent conflicting changes,\nthe existing `revision` must be specified when updating a contact.'
        )
        .optional(),
      source: z
        .object({
          sourceType: z
            .enum([
              'OTHER',
              'ADMIN',
              'WIX_APP',
              'IMPORT',
              'THIRD_PARTY',
              'WIX_BOOKINGS',
              'WIX_CHAT',
              'WIX_EMAIL_MARKETING',
              'WIX_EVENTS',
              'WIX_FORMS',
              'WIX_GROUPS',
              'WIX_HOTELS',
              'WIX_MARKET_PLACE',
              'WIX_MUSIC',
              'WIX_RESTAURANTS',
              'WIX_SITE_MEMBERS',
              'WIX_STORES',
              'WIX_CODE',
              'HOPP',
            ])
            .describe('Source type.')
            .optional(),
          appId: z
            .string()
            .describe('App ID, if the contact was created by an app.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe("Details about the contact's source.")
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the contact was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the contact was last updated.')
        .optional()
        .nullable(),
      lastActivity: z
        .object({
          activityDate: z
            .date()
            .describe('Date and time of the last action.')
            .optional()
            .nullable(),
          activityType: z
            .enum([
              'GENERAL',
              'CONTACT_CREATED',
              'MEMBER_LOGIN',
              'MEMBER_REGISTER',
              'MEMBER_STATUS_CHANGED',
              'FORM_SUBMITTED',
              'INBOX_FORM_SUBMITTED',
              'INBOX_PAYMENT_REQUEST_PAID',
              'INBOX_MESSAGE_TO_CUSTOMER',
              'INBOX_MESSAGE_FROM_CUSTOMER',
              'NEWSLETTER_SUBSCRIPTION_FORM_SUBMITTED',
              'NEWSLETTER_SUBSCRIPTION_UNSUBSCRIBE',
              'ECOM_PURCHASE',
              'ECOM_CART_ABANDON',
              'ECOM_CHECKOUT_BUYER',
              'BOOKINGS_APPOINTMENT',
              'HOTELS_RESERVATION',
              'HOTELS_PURCHASE',
              'HOTELS_CONFIRMATION',
              'HOTELS_CANCEL',
              'VIDEO_PURCHASE',
              'VIDEO_RENT',
              'CASHIER_BUTTON_PURCHASE',
              'ARENA_NEW_LEAD',
              'EVENTS_RSVP',
              'INVOICE_PAY',
              'INVOICE_OVERDUE',
              'PRICE_QUOTE_ACCEPT',
              'PRICE_QUOTE_EXPIRE',
              'RESTAURANTS_ORDER',
              'RESTAURANTS_RESERVATION',
              'SHOUTOUT_OPEN',
              'SHOUTOUT_CLICK',
              'CONTACT_MERGED',
              'NEWSLETTER_SUBSCRIPTION_SUBSCRIBE',
              'NEWSLETTER_SUBSCRIPTION_PENDING',
              'NEWSLETTER_SUBSCRIPTION_NOT_SET',
              'PHONE_SUBSCRIPTION_SUBSCRIBE',
              'PHONE_SUBSCRIPTION_PENDING',
              'PHONE_SUBSCRIPTION_NOT_SET',
              'PHONE_SUBSCRIPTION_UNSUBSCRIBE',
            ])
            .describe("Contact's last action in the site.")
            .optional(),
        })
        .describe("Details about the contact's last action in the site.")
        .optional(),
      primaryInfo: z
        .object({
          email: z
            .string()
            .describe(
              'Primary email address.\n\nThis property reflects the email address in `info.emails`\nwhere `primary` is `true`.'
            )
            .email()
            .optional()
            .nullable(),
          phone: z
            .string()
            .describe(
              'Primary phone number.\n\nThis property reflects the phone number in `info.phones`\nwhere `primary` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe("Contact's primary phone and email.")
        .optional(),
      info: z
        .object({
          name: z
            .object({
              first: z
                .string()
                .describe("Contact's first name.")
                .max(1000)
                .optional()
                .nullable(),
              last: z
                .string()
                .describe("Contact's last name.")
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe("Contact's first and last name.")
            .optional(),
          emails: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Email ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum(['UNTAGGED', 'MAIN', 'HOME', 'WORK'])
                      .describe(
                        'Email type.\n\n`UNTAGGED` is shown as "Other" in the Contact List.'
                      )
                      .optional(),
                    email: z
                      .string()
                      .describe('Email address.')
                      .min(1)
                      .max(320)
                      .optional(),
                    primary: z
                      .boolean()
                      .describe(
                        "Indicates whether this is the contact's primary email address.\nWhen changing `primary` to `true` for an email,\nthe contact's other emails become `false`.\nChanging the primary email of a contact also affects the subscription status to marketing emails that are decided based on the primary email."
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's email addresses.")
            .optional(),
          phones: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Phone ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum([
                        'UNTAGGED',
                        'MAIN',
                        'HOME',
                        'MOBILE',
                        'WORK',
                        'FAX',
                      ])
                      .describe(
                        'Phone type.\n\n`UNTAGGED` is shown as "Other" in the Contact List.'
                      )
                      .optional(),
                    countryCode: z
                      .string()
                      .describe(
                        '[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) country code.'
                      )
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Phone number.')
                      .min(1)
                      .max(50)
                      .optional(),
                    e164Phone: z
                      .string()
                      .describe(
                        '[ITU E.164-formatted](https://www.itu.int/rec/T-REC-E.164/)\nphone number.\nAutomatically generated using `phone` and `countryCode`, pending both values are valid.'
                      )
                      .optional()
                      .nullable(),
                    primary: z
                      .boolean()
                      .describe(
                        "Indicates whether this is the contact's primary phone number.\nWhen changing `primary` to `true` for a phone,\nthe contact's `primary` field for other phones becomes `false`.\nChanging the primary phone number also affects the subscription status to SMS messages that are decided based on the primary phone."
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's phone numbers.")
            .optional(),
          addresses: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Street address ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum(['UNTAGGED', 'HOME', 'WORK', 'BILLING', 'SHIPPING'])
                      .describe(
                        'Address type.\n`UNTAGGED` is shown as "Other" in the Contact List.'
                      )
                      .optional(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                        addressLine2: z.string().optional().nullable(),
                      })
                      .describe('Street address.')
                      .optional(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's street addresses.")
            .optional(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(1000)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(1000)
            .optional()
            .nullable(),
          birthdate: z
            .string()
            .describe(
              'Birth date in `YYYY-MM-DD` format. For example, `2020-03-15`.'
            )
            .optional()
            .nullable(),
          locale: z
            .string()
            .describe(
              'Locale in\n[IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nTypically, this is a lowercase 2-letter language code,\nfollowed by a hyphen, followed by an uppercase 2-letter country code.\nFor example, `en-US` for U.S. English, and `de-DE` for Germany German.'
            )
            .optional()
            .nullable(),
          labelKeys: z
            .object({ items: z.array(z.string()).max(2000).optional() })
            .describe(
              "List of contact's labels.\n\nLabels are used to organize contacts. Labels can be\nadded and removed using Label Contact and Unlabel Contact, respectively.\n\nTo view or manage contact labels, use the Labels API."
            )
            .optional(),
          extendedFields: z
            .object({
              items: z
                .record(z.string(), z.any())
                .describe(
                  "Contact's extended fields,\nwhere each key is the field key,\nand each value is the field's value for the contact.\n\nTo view and manage extended fields, use the Extended Fields API."
                )
                .optional(),
            })
            .describe(
              'Additional custom fields.\n\nEmpty fields are not returned.'
            )
            .optional(),
          picture: z
            .object({
              image: z
                .string()
                .describe(
                  'Image.\nThis can contain an image URL or a Wix Media image ID.'
                )
                .optional(),
            })
            .describe("Contact's profile picture.")
            .optional(),
        })
        .describe("Contact's details.")
        .optional(),
      primaryEmail: z
        .object({
          email: z
            .string()
            .describe(
              'Primary email address.\n\nThis field reflects the email address in `info.emails`\nwhere `primary` is `true`.'
            )
            .email()
            .optional()
            .nullable(),
          subscriptionStatus: z
            .enum([
              'NO_SUBSCRIPTION_STATUS',
              'NOT_SET',
              'PENDING',
              'SUBSCRIBED',
              'UNSUBSCRIBED',
            ])
            .describe(
              "Indicates the recipient's opt-in or opt-out status for marketing emails.\n\nDefault: `NOT_SET`"
            )
            .optional(),
          deliverabilityStatus: z
            .enum(['NOT_SET', 'VALID', 'BOUNCED', 'SPAM_COMPLAINT', 'INACTIVE'])
            .describe(
              'Indicates last reported status of sent emails.\n\nDefault: `NOT_SET`'
            )
            .optional(),
        })
        .describe("Contact's primary email details.")
        .optional(),
      primaryPhone: z
        .object({
          countryCode: z
            .string()
            .describe(
              'Country code of the primary phone number in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
            )
            .optional()
            .nullable(),
          e164Phone: z
            .string()
            .describe(
              'Phone number in [ITU E.164](https://www.itu.int/rec/T-REC-E.164/) format.\nAutomatically generated using `phone` and `countryCode` when both values are valid.'
            )
            .optional()
            .nullable(),
          formattedPhone: z
            .string()
            .describe(
              'Formatted phone. Automatically generated using `phone` and `countryCode`.'
            )
            .optional()
            .nullable(),
          subscriptionStatus: z
            .enum([
              'NO_SUBSCRIPTION_STATUS',
              'NOT_SET',
              'PENDING',
              'SUBSCRIBED',
              'UNSUBSCRIBED',
            ])
            .describe(
              "Indicates the recipient's opt-in or opt-out status for SMS messages.\n\nDefault:\n- `NOT_SET` when the phone is a valid E164 phone.\n- `NO_SUBSCRIPTION_STATUS` when the phone is not a valid E164 phone."
            )
            .optional(),
          phone: z
            .string()
            .describe(
              'Primary phone number.\n\nThis field reflects the phone number in `info.phones`\nwhere `primary` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe("Contact's primary phone details.")
        .optional(),
    })
    .describe('Updated target contact.')
    .optional(),
});
export const PreviewMergeContactsRequest = z.object({
  targetContactId: z
    .string()
    .describe('Target contact ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({ sourceContactIds: z.array(z.string()).min(1).max(5).optional() })
    .describe('Preview merge contacts options.')
    .optional(),
});
export const PreviewMergeContactsResponse = z.object({
  contact: z
    .object({
      _id: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      revision: z
        .number()
        .int()
        .describe(
          'Revision number, which increments by 1 each time the contact is updated.\nTo prevent conflicting changes,\nthe existing `revision` must be specified when updating a contact.'
        )
        .optional(),
      source: z
        .object({
          sourceType: z
            .enum([
              'OTHER',
              'ADMIN',
              'WIX_APP',
              'IMPORT',
              'THIRD_PARTY',
              'WIX_BOOKINGS',
              'WIX_CHAT',
              'WIX_EMAIL_MARKETING',
              'WIX_EVENTS',
              'WIX_FORMS',
              'WIX_GROUPS',
              'WIX_HOTELS',
              'WIX_MARKET_PLACE',
              'WIX_MUSIC',
              'WIX_RESTAURANTS',
              'WIX_SITE_MEMBERS',
              'WIX_STORES',
              'WIX_CODE',
              'HOPP',
            ])
            .describe('Source type.')
            .optional(),
          appId: z
            .string()
            .describe('App ID, if the contact was created by an app.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe("Details about the contact's source.")
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the contact was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the contact was last updated.')
        .optional()
        .nullable(),
      lastActivity: z
        .object({
          activityDate: z
            .date()
            .describe('Date and time of the last action.')
            .optional()
            .nullable(),
          activityType: z
            .enum([
              'GENERAL',
              'CONTACT_CREATED',
              'MEMBER_LOGIN',
              'MEMBER_REGISTER',
              'MEMBER_STATUS_CHANGED',
              'FORM_SUBMITTED',
              'INBOX_FORM_SUBMITTED',
              'INBOX_PAYMENT_REQUEST_PAID',
              'INBOX_MESSAGE_TO_CUSTOMER',
              'INBOX_MESSAGE_FROM_CUSTOMER',
              'NEWSLETTER_SUBSCRIPTION_FORM_SUBMITTED',
              'NEWSLETTER_SUBSCRIPTION_UNSUBSCRIBE',
              'ECOM_PURCHASE',
              'ECOM_CART_ABANDON',
              'ECOM_CHECKOUT_BUYER',
              'BOOKINGS_APPOINTMENT',
              'HOTELS_RESERVATION',
              'HOTELS_PURCHASE',
              'HOTELS_CONFIRMATION',
              'HOTELS_CANCEL',
              'VIDEO_PURCHASE',
              'VIDEO_RENT',
              'CASHIER_BUTTON_PURCHASE',
              'ARENA_NEW_LEAD',
              'EVENTS_RSVP',
              'INVOICE_PAY',
              'INVOICE_OVERDUE',
              'PRICE_QUOTE_ACCEPT',
              'PRICE_QUOTE_EXPIRE',
              'RESTAURANTS_ORDER',
              'RESTAURANTS_RESERVATION',
              'SHOUTOUT_OPEN',
              'SHOUTOUT_CLICK',
              'CONTACT_MERGED',
              'NEWSLETTER_SUBSCRIPTION_SUBSCRIBE',
              'NEWSLETTER_SUBSCRIPTION_PENDING',
              'NEWSLETTER_SUBSCRIPTION_NOT_SET',
              'PHONE_SUBSCRIPTION_SUBSCRIBE',
              'PHONE_SUBSCRIPTION_PENDING',
              'PHONE_SUBSCRIPTION_NOT_SET',
              'PHONE_SUBSCRIPTION_UNSUBSCRIBE',
            ])
            .describe("Contact's last action in the site.")
            .optional(),
        })
        .describe("Details about the contact's last action in the site.")
        .optional(),
      primaryInfo: z
        .object({
          email: z
            .string()
            .describe(
              'Primary email address.\n\nThis property reflects the email address in `info.emails`\nwhere `primary` is `true`.'
            )
            .email()
            .optional()
            .nullable(),
          phone: z
            .string()
            .describe(
              'Primary phone number.\n\nThis property reflects the phone number in `info.phones`\nwhere `primary` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe("Contact's primary phone and email.")
        .optional(),
      info: z
        .object({
          name: z
            .object({
              first: z
                .string()
                .describe("Contact's first name.")
                .max(1000)
                .optional()
                .nullable(),
              last: z
                .string()
                .describe("Contact's last name.")
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe("Contact's first and last name.")
            .optional(),
          emails: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Email ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum(['UNTAGGED', 'MAIN', 'HOME', 'WORK'])
                      .describe(
                        'Email type.\n\n`UNTAGGED` is shown as "Other" in the Contact List.'
                      )
                      .optional(),
                    email: z
                      .string()
                      .describe('Email address.')
                      .min(1)
                      .max(320)
                      .optional(),
                    primary: z
                      .boolean()
                      .describe(
                        "Indicates whether this is the contact's primary email address.\nWhen changing `primary` to `true` for an email,\nthe contact's other emails become `false`.\nChanging the primary email of a contact also affects the subscription status to marketing emails that are decided based on the primary email."
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's email addresses.")
            .optional(),
          phones: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Phone ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum([
                        'UNTAGGED',
                        'MAIN',
                        'HOME',
                        'MOBILE',
                        'WORK',
                        'FAX',
                      ])
                      .describe(
                        'Phone type.\n\n`UNTAGGED` is shown as "Other" in the Contact List.'
                      )
                      .optional(),
                    countryCode: z
                      .string()
                      .describe(
                        '[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) country code.'
                      )
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Phone number.')
                      .min(1)
                      .max(50)
                      .optional(),
                    e164Phone: z
                      .string()
                      .describe(
                        '[ITU E.164-formatted](https://www.itu.int/rec/T-REC-E.164/)\nphone number.\nAutomatically generated using `phone` and `countryCode`, pending both values are valid.'
                      )
                      .optional()
                      .nullable(),
                    primary: z
                      .boolean()
                      .describe(
                        "Indicates whether this is the contact's primary phone number.\nWhen changing `primary` to `true` for a phone,\nthe contact's `primary` field for other phones becomes `false`.\nChanging the primary phone number also affects the subscription status to SMS messages that are decided based on the primary phone."
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's phone numbers.")
            .optional(),
          addresses: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Street address ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum(['UNTAGGED', 'HOME', 'WORK', 'BILLING', 'SHIPPING'])
                      .describe(
                        'Address type.\n`UNTAGGED` is shown as "Other" in the Contact List.'
                      )
                      .optional(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                        addressLine2: z.string().optional().nullable(),
                      })
                      .describe('Street address.')
                      .optional(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's street addresses.")
            .optional(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(1000)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(1000)
            .optional()
            .nullable(),
          birthdate: z
            .string()
            .describe(
              'Birth date in `YYYY-MM-DD` format. For example, `2020-03-15`.'
            )
            .optional()
            .nullable(),
          locale: z
            .string()
            .describe(
              'Locale in\n[IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nTypically, this is a lowercase 2-letter language code,\nfollowed by a hyphen, followed by an uppercase 2-letter country code.\nFor example, `en-US` for U.S. English, and `de-DE` for Germany German.'
            )
            .optional()
            .nullable(),
          labelKeys: z
            .object({ items: z.array(z.string()).max(2000).optional() })
            .describe(
              "List of contact's labels.\n\nLabels are used to organize contacts. Labels can be\nadded and removed using Label Contact and Unlabel Contact, respectively.\n\nTo view or manage contact labels, use the Labels API."
            )
            .optional(),
          extendedFields: z
            .object({
              items: z
                .record(z.string(), z.any())
                .describe(
                  "Contact's extended fields,\nwhere each key is the field key,\nand each value is the field's value for the contact.\n\nTo view and manage extended fields, use the Extended Fields API."
                )
                .optional(),
            })
            .describe(
              'Additional custom fields.\n\nEmpty fields are not returned.'
            )
            .optional(),
          picture: z
            .object({
              image: z
                .string()
                .describe(
                  'Image.\nThis can contain an image URL or a Wix Media image ID.'
                )
                .optional(),
            })
            .describe("Contact's profile picture.")
            .optional(),
        })
        .describe("Contact's details.")
        .optional(),
      primaryEmail: z
        .object({
          email: z
            .string()
            .describe(
              'Primary email address.\n\nThis field reflects the email address in `info.emails`\nwhere `primary` is `true`.'
            )
            .email()
            .optional()
            .nullable(),
          subscriptionStatus: z
            .enum([
              'NO_SUBSCRIPTION_STATUS',
              'NOT_SET',
              'PENDING',
              'SUBSCRIBED',
              'UNSUBSCRIBED',
            ])
            .describe(
              "Indicates the recipient's opt-in or opt-out status for marketing emails.\n\nDefault: `NOT_SET`"
            )
            .optional(),
          deliverabilityStatus: z
            .enum(['NOT_SET', 'VALID', 'BOUNCED', 'SPAM_COMPLAINT', 'INACTIVE'])
            .describe(
              'Indicates last reported status of sent emails.\n\nDefault: `NOT_SET`'
            )
            .optional(),
        })
        .describe("Contact's primary email details.")
        .optional(),
      primaryPhone: z
        .object({
          countryCode: z
            .string()
            .describe(
              'Country code of the primary phone number in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
            )
            .optional()
            .nullable(),
          e164Phone: z
            .string()
            .describe(
              'Phone number in [ITU E.164](https://www.itu.int/rec/T-REC-E.164/) format.\nAutomatically generated using `phone` and `countryCode` when both values are valid.'
            )
            .optional()
            .nullable(),
          formattedPhone: z
            .string()
            .describe(
              'Formatted phone. Automatically generated using `phone` and `countryCode`.'
            )
            .optional()
            .nullable(),
          subscriptionStatus: z
            .enum([
              'NO_SUBSCRIPTION_STATUS',
              'NOT_SET',
              'PENDING',
              'SUBSCRIBED',
              'UNSUBSCRIBED',
            ])
            .describe(
              "Indicates the recipient's opt-in or opt-out status for SMS messages.\n\nDefault:\n- `NOT_SET` when the phone is a valid E164 phone.\n- `NO_SUBSCRIPTION_STATUS` when the phone is not a valid E164 phone."
            )
            .optional(),
          phone: z
            .string()
            .describe(
              'Primary phone number.\n\nThis field reflects the phone number in `info.phones`\nwhere `primary` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe("Contact's primary phone details.")
        .optional(),
    })
    .describe('Preview of the updated target contact.')
    .optional(),
});
export const DeleteContactRequest = z.object({
  contactId: z
    .string()
    .describe('ID of the contact to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteContactResponse = z.object({});
export const LabelContactRequest = z.object({
  contactId: z
    .string()
    .describe('ID of the contact to add labels to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  labelKeys: z.array(z.string()).min(1).max(100),
});
export const LabelContactResponse = z.object({
  contact: z
    .object({
      _id: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      revision: z
        .number()
        .int()
        .describe(
          'Revision number, which increments by 1 each time the contact is updated.\nTo prevent conflicting changes,\nthe existing `revision` must be specified when updating a contact.'
        )
        .optional(),
      source: z
        .object({
          sourceType: z
            .enum([
              'OTHER',
              'ADMIN',
              'WIX_APP',
              'IMPORT',
              'THIRD_PARTY',
              'WIX_BOOKINGS',
              'WIX_CHAT',
              'WIX_EMAIL_MARKETING',
              'WIX_EVENTS',
              'WIX_FORMS',
              'WIX_GROUPS',
              'WIX_HOTELS',
              'WIX_MARKET_PLACE',
              'WIX_MUSIC',
              'WIX_RESTAURANTS',
              'WIX_SITE_MEMBERS',
              'WIX_STORES',
              'WIX_CODE',
              'HOPP',
            ])
            .describe('Source type.')
            .optional(),
          appId: z
            .string()
            .describe('App ID, if the contact was created by an app.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe("Details about the contact's source.")
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the contact was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the contact was last updated.')
        .optional()
        .nullable(),
      lastActivity: z
        .object({
          activityDate: z
            .date()
            .describe('Date and time of the last action.')
            .optional()
            .nullable(),
          activityType: z
            .enum([
              'GENERAL',
              'CONTACT_CREATED',
              'MEMBER_LOGIN',
              'MEMBER_REGISTER',
              'MEMBER_STATUS_CHANGED',
              'FORM_SUBMITTED',
              'INBOX_FORM_SUBMITTED',
              'INBOX_PAYMENT_REQUEST_PAID',
              'INBOX_MESSAGE_TO_CUSTOMER',
              'INBOX_MESSAGE_FROM_CUSTOMER',
              'NEWSLETTER_SUBSCRIPTION_FORM_SUBMITTED',
              'NEWSLETTER_SUBSCRIPTION_UNSUBSCRIBE',
              'ECOM_PURCHASE',
              'ECOM_CART_ABANDON',
              'ECOM_CHECKOUT_BUYER',
              'BOOKINGS_APPOINTMENT',
              'HOTELS_RESERVATION',
              'HOTELS_PURCHASE',
              'HOTELS_CONFIRMATION',
              'HOTELS_CANCEL',
              'VIDEO_PURCHASE',
              'VIDEO_RENT',
              'CASHIER_BUTTON_PURCHASE',
              'ARENA_NEW_LEAD',
              'EVENTS_RSVP',
              'INVOICE_PAY',
              'INVOICE_OVERDUE',
              'PRICE_QUOTE_ACCEPT',
              'PRICE_QUOTE_EXPIRE',
              'RESTAURANTS_ORDER',
              'RESTAURANTS_RESERVATION',
              'SHOUTOUT_OPEN',
              'SHOUTOUT_CLICK',
              'CONTACT_MERGED',
              'NEWSLETTER_SUBSCRIPTION_SUBSCRIBE',
              'NEWSLETTER_SUBSCRIPTION_PENDING',
              'NEWSLETTER_SUBSCRIPTION_NOT_SET',
              'PHONE_SUBSCRIPTION_SUBSCRIBE',
              'PHONE_SUBSCRIPTION_PENDING',
              'PHONE_SUBSCRIPTION_NOT_SET',
              'PHONE_SUBSCRIPTION_UNSUBSCRIBE',
            ])
            .describe("Contact's last action in the site.")
            .optional(),
        })
        .describe("Details about the contact's last action in the site.")
        .optional(),
      primaryInfo: z
        .object({
          email: z
            .string()
            .describe(
              'Primary email address.\n\nThis property reflects the email address in `info.emails`\nwhere `primary` is `true`.'
            )
            .email()
            .optional()
            .nullable(),
          phone: z
            .string()
            .describe(
              'Primary phone number.\n\nThis property reflects the phone number in `info.phones`\nwhere `primary` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe("Contact's primary phone and email.")
        .optional(),
      info: z
        .object({
          name: z
            .object({
              first: z
                .string()
                .describe("Contact's first name.")
                .max(1000)
                .optional()
                .nullable(),
              last: z
                .string()
                .describe("Contact's last name.")
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe("Contact's first and last name.")
            .optional(),
          emails: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Email ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum(['UNTAGGED', 'MAIN', 'HOME', 'WORK'])
                      .describe(
                        'Email type.\n\n`UNTAGGED` is shown as "Other" in the Contact List.'
                      )
                      .optional(),
                    email: z
                      .string()
                      .describe('Email address.')
                      .min(1)
                      .max(320)
                      .optional(),
                    primary: z
                      .boolean()
                      .describe(
                        "Indicates whether this is the contact's primary email address.\nWhen changing `primary` to `true` for an email,\nthe contact's other emails become `false`.\nChanging the primary email of a contact also affects the subscription status to marketing emails that are decided based on the primary email."
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's email addresses.")
            .optional(),
          phones: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Phone ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum([
                        'UNTAGGED',
                        'MAIN',
                        'HOME',
                        'MOBILE',
                        'WORK',
                        'FAX',
                      ])
                      .describe(
                        'Phone type.\n\n`UNTAGGED` is shown as "Other" in the Contact List.'
                      )
                      .optional(),
                    countryCode: z
                      .string()
                      .describe(
                        '[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) country code.'
                      )
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Phone number.')
                      .min(1)
                      .max(50)
                      .optional(),
                    e164Phone: z
                      .string()
                      .describe(
                        '[ITU E.164-formatted](https://www.itu.int/rec/T-REC-E.164/)\nphone number.\nAutomatically generated using `phone` and `countryCode`, pending both values are valid.'
                      )
                      .optional()
                      .nullable(),
                    primary: z
                      .boolean()
                      .describe(
                        "Indicates whether this is the contact's primary phone number.\nWhen changing `primary` to `true` for a phone,\nthe contact's `primary` field for other phones becomes `false`.\nChanging the primary phone number also affects the subscription status to SMS messages that are decided based on the primary phone."
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's phone numbers.")
            .optional(),
          addresses: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Street address ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum(['UNTAGGED', 'HOME', 'WORK', 'BILLING', 'SHIPPING'])
                      .describe(
                        'Address type.\n`UNTAGGED` is shown as "Other" in the Contact List.'
                      )
                      .optional(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                        addressLine2: z.string().optional().nullable(),
                      })
                      .describe('Street address.')
                      .optional(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's street addresses.")
            .optional(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(1000)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(1000)
            .optional()
            .nullable(),
          birthdate: z
            .string()
            .describe(
              'Birth date in `YYYY-MM-DD` format. For example, `2020-03-15`.'
            )
            .optional()
            .nullable(),
          locale: z
            .string()
            .describe(
              'Locale in\n[IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nTypically, this is a lowercase 2-letter language code,\nfollowed by a hyphen, followed by an uppercase 2-letter country code.\nFor example, `en-US` for U.S. English, and `de-DE` for Germany German.'
            )
            .optional()
            .nullable(),
          labelKeys: z
            .object({ items: z.array(z.string()).max(2000).optional() })
            .describe(
              "List of contact's labels.\n\nLabels are used to organize contacts. Labels can be\nadded and removed using Label Contact and Unlabel Contact, respectively.\n\nTo view or manage contact labels, use the Labels API."
            )
            .optional(),
          extendedFields: z
            .object({
              items: z
                .record(z.string(), z.any())
                .describe(
                  "Contact's extended fields,\nwhere each key is the field key,\nand each value is the field's value for the contact.\n\nTo view and manage extended fields, use the Extended Fields API."
                )
                .optional(),
            })
            .describe(
              'Additional custom fields.\n\nEmpty fields are not returned.'
            )
            .optional(),
          picture: z
            .object({
              image: z
                .string()
                .describe(
                  'Image.\nThis can contain an image URL or a Wix Media image ID.'
                )
                .optional(),
            })
            .describe("Contact's profile picture.")
            .optional(),
        })
        .describe("Contact's details.")
        .optional(),
      primaryEmail: z
        .object({
          email: z
            .string()
            .describe(
              'Primary email address.\n\nThis field reflects the email address in `info.emails`\nwhere `primary` is `true`.'
            )
            .email()
            .optional()
            .nullable(),
          subscriptionStatus: z
            .enum([
              'NO_SUBSCRIPTION_STATUS',
              'NOT_SET',
              'PENDING',
              'SUBSCRIBED',
              'UNSUBSCRIBED',
            ])
            .describe(
              "Indicates the recipient's opt-in or opt-out status for marketing emails.\n\nDefault: `NOT_SET`"
            )
            .optional(),
          deliverabilityStatus: z
            .enum(['NOT_SET', 'VALID', 'BOUNCED', 'SPAM_COMPLAINT', 'INACTIVE'])
            .describe(
              'Indicates last reported status of sent emails.\n\nDefault: `NOT_SET`'
            )
            .optional(),
        })
        .describe("Contact's primary email details.")
        .optional(),
      primaryPhone: z
        .object({
          countryCode: z
            .string()
            .describe(
              'Country code of the primary phone number in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
            )
            .optional()
            .nullable(),
          e164Phone: z
            .string()
            .describe(
              'Phone number in [ITU E.164](https://www.itu.int/rec/T-REC-E.164/) format.\nAutomatically generated using `phone` and `countryCode` when both values are valid.'
            )
            .optional()
            .nullable(),
          formattedPhone: z
            .string()
            .describe(
              'Formatted phone. Automatically generated using `phone` and `countryCode`.'
            )
            .optional()
            .nullable(),
          subscriptionStatus: z
            .enum([
              'NO_SUBSCRIPTION_STATUS',
              'NOT_SET',
              'PENDING',
              'SUBSCRIBED',
              'UNSUBSCRIBED',
            ])
            .describe(
              "Indicates the recipient's opt-in or opt-out status for SMS messages.\n\nDefault:\n- `NOT_SET` when the phone is a valid E164 phone.\n- `NO_SUBSCRIPTION_STATUS` when the phone is not a valid E164 phone."
            )
            .optional(),
          phone: z
            .string()
            .describe(
              'Primary phone number.\n\nThis field reflects the phone number in `info.phones`\nwhere `primary` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe("Contact's primary phone details.")
        .optional(),
    })
    .describe('Updated contact.')
    .optional(),
});
export const UnlabelContactRequest = z.object({
  contactId: z
    .string()
    .describe('ID of the contact to remove labels from.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  labelKeys: z.array(z.string()).min(1).max(100),
});
export const UnlabelContactResponse = z.object({
  contact: z
    .object({
      _id: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      revision: z
        .number()
        .int()
        .describe(
          'Revision number, which increments by 1 each time the contact is updated.\nTo prevent conflicting changes,\nthe existing `revision` must be specified when updating a contact.'
        )
        .optional(),
      source: z
        .object({
          sourceType: z
            .enum([
              'OTHER',
              'ADMIN',
              'WIX_APP',
              'IMPORT',
              'THIRD_PARTY',
              'WIX_BOOKINGS',
              'WIX_CHAT',
              'WIX_EMAIL_MARKETING',
              'WIX_EVENTS',
              'WIX_FORMS',
              'WIX_GROUPS',
              'WIX_HOTELS',
              'WIX_MARKET_PLACE',
              'WIX_MUSIC',
              'WIX_RESTAURANTS',
              'WIX_SITE_MEMBERS',
              'WIX_STORES',
              'WIX_CODE',
              'HOPP',
            ])
            .describe('Source type.')
            .optional(),
          appId: z
            .string()
            .describe('App ID, if the contact was created by an app.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe("Details about the contact's source.")
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the contact was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the contact was last updated.')
        .optional()
        .nullable(),
      lastActivity: z
        .object({
          activityDate: z
            .date()
            .describe('Date and time of the last action.')
            .optional()
            .nullable(),
          activityType: z
            .enum([
              'GENERAL',
              'CONTACT_CREATED',
              'MEMBER_LOGIN',
              'MEMBER_REGISTER',
              'MEMBER_STATUS_CHANGED',
              'FORM_SUBMITTED',
              'INBOX_FORM_SUBMITTED',
              'INBOX_PAYMENT_REQUEST_PAID',
              'INBOX_MESSAGE_TO_CUSTOMER',
              'INBOX_MESSAGE_FROM_CUSTOMER',
              'NEWSLETTER_SUBSCRIPTION_FORM_SUBMITTED',
              'NEWSLETTER_SUBSCRIPTION_UNSUBSCRIBE',
              'ECOM_PURCHASE',
              'ECOM_CART_ABANDON',
              'ECOM_CHECKOUT_BUYER',
              'BOOKINGS_APPOINTMENT',
              'HOTELS_RESERVATION',
              'HOTELS_PURCHASE',
              'HOTELS_CONFIRMATION',
              'HOTELS_CANCEL',
              'VIDEO_PURCHASE',
              'VIDEO_RENT',
              'CASHIER_BUTTON_PURCHASE',
              'ARENA_NEW_LEAD',
              'EVENTS_RSVP',
              'INVOICE_PAY',
              'INVOICE_OVERDUE',
              'PRICE_QUOTE_ACCEPT',
              'PRICE_QUOTE_EXPIRE',
              'RESTAURANTS_ORDER',
              'RESTAURANTS_RESERVATION',
              'SHOUTOUT_OPEN',
              'SHOUTOUT_CLICK',
              'CONTACT_MERGED',
              'NEWSLETTER_SUBSCRIPTION_SUBSCRIBE',
              'NEWSLETTER_SUBSCRIPTION_PENDING',
              'NEWSLETTER_SUBSCRIPTION_NOT_SET',
              'PHONE_SUBSCRIPTION_SUBSCRIBE',
              'PHONE_SUBSCRIPTION_PENDING',
              'PHONE_SUBSCRIPTION_NOT_SET',
              'PHONE_SUBSCRIPTION_UNSUBSCRIBE',
            ])
            .describe("Contact's last action in the site.")
            .optional(),
        })
        .describe("Details about the contact's last action in the site.")
        .optional(),
      primaryInfo: z
        .object({
          email: z
            .string()
            .describe(
              'Primary email address.\n\nThis property reflects the email address in `info.emails`\nwhere `primary` is `true`.'
            )
            .email()
            .optional()
            .nullable(),
          phone: z
            .string()
            .describe(
              'Primary phone number.\n\nThis property reflects the phone number in `info.phones`\nwhere `primary` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe("Contact's primary phone and email.")
        .optional(),
      info: z
        .object({
          name: z
            .object({
              first: z
                .string()
                .describe("Contact's first name.")
                .max(1000)
                .optional()
                .nullable(),
              last: z
                .string()
                .describe("Contact's last name.")
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe("Contact's first and last name.")
            .optional(),
          emails: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Email ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum(['UNTAGGED', 'MAIN', 'HOME', 'WORK'])
                      .describe(
                        'Email type.\n\n`UNTAGGED` is shown as "Other" in the Contact List.'
                      )
                      .optional(),
                    email: z
                      .string()
                      .describe('Email address.')
                      .min(1)
                      .max(320)
                      .optional(),
                    primary: z
                      .boolean()
                      .describe(
                        "Indicates whether this is the contact's primary email address.\nWhen changing `primary` to `true` for an email,\nthe contact's other emails become `false`.\nChanging the primary email of a contact also affects the subscription status to marketing emails that are decided based on the primary email."
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's email addresses.")
            .optional(),
          phones: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Phone ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum([
                        'UNTAGGED',
                        'MAIN',
                        'HOME',
                        'MOBILE',
                        'WORK',
                        'FAX',
                      ])
                      .describe(
                        'Phone type.\n\n`UNTAGGED` is shown as "Other" in the Contact List.'
                      )
                      .optional(),
                    countryCode: z
                      .string()
                      .describe(
                        '[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) country code.'
                      )
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Phone number.')
                      .min(1)
                      .max(50)
                      .optional(),
                    e164Phone: z
                      .string()
                      .describe(
                        '[ITU E.164-formatted](https://www.itu.int/rec/T-REC-E.164/)\nphone number.\nAutomatically generated using `phone` and `countryCode`, pending both values are valid.'
                      )
                      .optional()
                      .nullable(),
                    primary: z
                      .boolean()
                      .describe(
                        "Indicates whether this is the contact's primary phone number.\nWhen changing `primary` to `true` for a phone,\nthe contact's `primary` field for other phones becomes `false`.\nChanging the primary phone number also affects the subscription status to SMS messages that are decided based on the primary phone."
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's phone numbers.")
            .optional(),
          addresses: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Street address ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum(['UNTAGGED', 'HOME', 'WORK', 'BILLING', 'SHIPPING'])
                      .describe(
                        'Address type.\n`UNTAGGED` is shown as "Other" in the Contact List.'
                      )
                      .optional(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                        addressLine2: z.string().optional().nullable(),
                      })
                      .describe('Street address.')
                      .optional(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's street addresses.")
            .optional(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(1000)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(1000)
            .optional()
            .nullable(),
          birthdate: z
            .string()
            .describe(
              'Birth date in `YYYY-MM-DD` format. For example, `2020-03-15`.'
            )
            .optional()
            .nullable(),
          locale: z
            .string()
            .describe(
              'Locale in\n[IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nTypically, this is a lowercase 2-letter language code,\nfollowed by a hyphen, followed by an uppercase 2-letter country code.\nFor example, `en-US` for U.S. English, and `de-DE` for Germany German.'
            )
            .optional()
            .nullable(),
          labelKeys: z
            .object({ items: z.array(z.string()).max(2000).optional() })
            .describe(
              "List of contact's labels.\n\nLabels are used to organize contacts. Labels can be\nadded and removed using Label Contact and Unlabel Contact, respectively.\n\nTo view or manage contact labels, use the Labels API."
            )
            .optional(),
          extendedFields: z
            .object({
              items: z
                .record(z.string(), z.any())
                .describe(
                  "Contact's extended fields,\nwhere each key is the field key,\nand each value is the field's value for the contact.\n\nTo view and manage extended fields, use the Extended Fields API."
                )
                .optional(),
            })
            .describe(
              'Additional custom fields.\n\nEmpty fields are not returned.'
            )
            .optional(),
          picture: z
            .object({
              image: z
                .string()
                .describe(
                  'Image.\nThis can contain an image URL or a Wix Media image ID.'
                )
                .optional(),
            })
            .describe("Contact's profile picture.")
            .optional(),
        })
        .describe("Contact's details.")
        .optional(),
      primaryEmail: z
        .object({
          email: z
            .string()
            .describe(
              'Primary email address.\n\nThis field reflects the email address in `info.emails`\nwhere `primary` is `true`.'
            )
            .email()
            .optional()
            .nullable(),
          subscriptionStatus: z
            .enum([
              'NO_SUBSCRIPTION_STATUS',
              'NOT_SET',
              'PENDING',
              'SUBSCRIBED',
              'UNSUBSCRIBED',
            ])
            .describe(
              "Indicates the recipient's opt-in or opt-out status for marketing emails.\n\nDefault: `NOT_SET`"
            )
            .optional(),
          deliverabilityStatus: z
            .enum(['NOT_SET', 'VALID', 'BOUNCED', 'SPAM_COMPLAINT', 'INACTIVE'])
            .describe(
              'Indicates last reported status of sent emails.\n\nDefault: `NOT_SET`'
            )
            .optional(),
        })
        .describe("Contact's primary email details.")
        .optional(),
      primaryPhone: z
        .object({
          countryCode: z
            .string()
            .describe(
              'Country code of the primary phone number in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
            )
            .optional()
            .nullable(),
          e164Phone: z
            .string()
            .describe(
              'Phone number in [ITU E.164](https://www.itu.int/rec/T-REC-E.164/) format.\nAutomatically generated using `phone` and `countryCode` when both values are valid.'
            )
            .optional()
            .nullable(),
          formattedPhone: z
            .string()
            .describe(
              'Formatted phone. Automatically generated using `phone` and `countryCode`.'
            )
            .optional()
            .nullable(),
          subscriptionStatus: z
            .enum([
              'NO_SUBSCRIPTION_STATUS',
              'NOT_SET',
              'PENDING',
              'SUBSCRIBED',
              'UNSUBSCRIBED',
            ])
            .describe(
              "Indicates the recipient's opt-in or opt-out status for SMS messages.\n\nDefault:\n- `NOT_SET` when the phone is a valid E164 phone.\n- `NO_SUBSCRIPTION_STATUS` when the phone is not a valid E164 phone."
            )
            .optional(),
          phone: z
            .string()
            .describe(
              'Primary phone number.\n\nThis field reflects the phone number in `info.phones`\nwhere `primary` is `true`.'
            )
            .optional()
            .nullable(),
        })
        .describe("Contact's primary phone details.")
        .optional(),
    })
    .describe('Updated contact.')
    .optional(),
});
export const ListContactsRequest = z.object({
  options: z
    .object({
      sort: z
        .object({
          fieldName: z
            .string()
            .describe(
              'Field to sort by.\n\nSupported properties:\n`createdDate`, `lastActivity.activityDate`, `primaryInfo.email`, `info.name.first`, `info.name.last`, `info.company`,\n`info.jobTitle`, `info.birthdate`'
            )
            .max(100)
            .optional(),
          order: z.enum(['ASC', 'DESC']).optional(),
        })
        .describe(
          '[Sort order](https://dev.wix.com/api/rest/contacts/contacts/sort-and-filter).'
        )
        .optional(),
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe(
              'Number of items to return. Learn more about pagination in [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#paging).\n\nDefault: `50`. <br>\nMaximum: `1000`.'
            )
            .min(0)
            .max(1000)
            .optional()
            .nullable(),
          offset: z
            .number()
            .int()
            .describe('Number of items to skip in the current sort order.')
            .min(0)
            .optional()
            .nullable(),
        })
        .describe(
          '[Pagination](https://dev.wix.com/api/rest/getting-started/pagination), defaults to offset = 0 and limit = 50 (max limit 1,000).'
        )
        .optional(),
      fields: z.array(z.string()).max(100).optional(),
      fieldsets: z
        .array(z.enum(['BASIC', 'COMMUNICATION_DETAILS', 'EXTENDED', 'FULL']))
        .max(10)
        .optional(),
    })
    .describe('Object containing list of options for retrieving contacts.')
    .optional(),
});
export const ListContactsResponse = z.object({
  contacts: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Contact ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        revision: z
          .number()
          .int()
          .describe(
            'Revision number, which increments by 1 each time the contact is updated.\nTo prevent conflicting changes,\nthe existing `revision` must be specified when updating a contact.'
          )
          .optional(),
        source: z
          .object({
            sourceType: z
              .enum([
                'OTHER',
                'ADMIN',
                'WIX_APP',
                'IMPORT',
                'THIRD_PARTY',
                'WIX_BOOKINGS',
                'WIX_CHAT',
                'WIX_EMAIL_MARKETING',
                'WIX_EVENTS',
                'WIX_FORMS',
                'WIX_GROUPS',
                'WIX_HOTELS',
                'WIX_MARKET_PLACE',
                'WIX_MUSIC',
                'WIX_RESTAURANTS',
                'WIX_SITE_MEMBERS',
                'WIX_STORES',
                'WIX_CODE',
                'HOPP',
              ])
              .describe('Source type.')
              .optional(),
            appId: z
              .string()
              .describe('App ID, if the contact was created by an app.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
          .describe("Details about the contact's source.")
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time the contact was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the contact was last updated.')
          .optional()
          .nullable(),
        lastActivity: z
          .object({
            activityDate: z
              .date()
              .describe('Date and time of the last action.')
              .optional()
              .nullable(),
            activityType: z
              .enum([
                'GENERAL',
                'CONTACT_CREATED',
                'MEMBER_LOGIN',
                'MEMBER_REGISTER',
                'MEMBER_STATUS_CHANGED',
                'FORM_SUBMITTED',
                'INBOX_FORM_SUBMITTED',
                'INBOX_PAYMENT_REQUEST_PAID',
                'INBOX_MESSAGE_TO_CUSTOMER',
                'INBOX_MESSAGE_FROM_CUSTOMER',
                'NEWSLETTER_SUBSCRIPTION_FORM_SUBMITTED',
                'NEWSLETTER_SUBSCRIPTION_UNSUBSCRIBE',
                'ECOM_PURCHASE',
                'ECOM_CART_ABANDON',
                'ECOM_CHECKOUT_BUYER',
                'BOOKINGS_APPOINTMENT',
                'HOTELS_RESERVATION',
                'HOTELS_PURCHASE',
                'HOTELS_CONFIRMATION',
                'HOTELS_CANCEL',
                'VIDEO_PURCHASE',
                'VIDEO_RENT',
                'CASHIER_BUTTON_PURCHASE',
                'ARENA_NEW_LEAD',
                'EVENTS_RSVP',
                'INVOICE_PAY',
                'INVOICE_OVERDUE',
                'PRICE_QUOTE_ACCEPT',
                'PRICE_QUOTE_EXPIRE',
                'RESTAURANTS_ORDER',
                'RESTAURANTS_RESERVATION',
                'SHOUTOUT_OPEN',
                'SHOUTOUT_CLICK',
                'CONTACT_MERGED',
                'NEWSLETTER_SUBSCRIPTION_SUBSCRIBE',
                'NEWSLETTER_SUBSCRIPTION_PENDING',
                'NEWSLETTER_SUBSCRIPTION_NOT_SET',
                'PHONE_SUBSCRIPTION_SUBSCRIBE',
                'PHONE_SUBSCRIPTION_PENDING',
                'PHONE_SUBSCRIPTION_NOT_SET',
                'PHONE_SUBSCRIPTION_UNSUBSCRIBE',
              ])
              .describe("Contact's last action in the site.")
              .optional(),
          })
          .describe("Details about the contact's last action in the site.")
          .optional(),
        primaryInfo: z
          .object({
            email: z
              .string()
              .describe(
                'Primary email address.\n\nThis property reflects the email address in `info.emails`\nwhere `primary` is `true`.'
              )
              .email()
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe(
                'Primary phone number.\n\nThis property reflects the phone number in `info.phones`\nwhere `primary` is `true`.'
              )
              .optional()
              .nullable(),
          })
          .describe("Contact's primary phone and email.")
          .optional(),
        info: z
          .object({
            name: z
              .object({
                first: z
                  .string()
                  .describe("Contact's first name.")
                  .max(1000)
                  .optional()
                  .nullable(),
                last: z
                  .string()
                  .describe("Contact's last name.")
                  .max(1000)
                  .optional()
                  .nullable(),
              })
              .describe("Contact's first and last name.")
              .optional(),
            emails: z
              .object({
                items: z
                  .array(
                    z.object({
                      _id: z
                        .string()
                        .describe('Email ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      tag: z
                        .enum(['UNTAGGED', 'MAIN', 'HOME', 'WORK'])
                        .describe(
                          'Email type.\n\n`UNTAGGED` is shown as "Other" in the Contact List.'
                        )
                        .optional(),
                      email: z
                        .string()
                        .describe('Email address.')
                        .min(1)
                        .max(320)
                        .optional(),
                      primary: z
                        .boolean()
                        .describe(
                          "Indicates whether this is the contact's primary email address.\nWhen changing `primary` to `true` for an email,\nthe contact's other emails become `false`.\nChanging the primary email of a contact also affects the subscription status to marketing emails that are decided based on the primary email."
                        )
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(50)
                  .optional(),
              })
              .describe("Contact's email addresses.")
              .optional(),
            phones: z
              .object({
                items: z
                  .array(
                    z.object({
                      _id: z
                        .string()
                        .describe('Phone ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      tag: z
                        .enum([
                          'UNTAGGED',
                          'MAIN',
                          'HOME',
                          'MOBILE',
                          'WORK',
                          'FAX',
                        ])
                        .describe(
                          'Phone type.\n\n`UNTAGGED` is shown as "Other" in the Contact List.'
                        )
                        .optional(),
                      countryCode: z
                        .string()
                        .describe(
                          '[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) country code.'
                        )
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe('Phone number.')
                        .min(1)
                        .max(50)
                        .optional(),
                      e164Phone: z
                        .string()
                        .describe(
                          '[ITU E.164-formatted](https://www.itu.int/rec/T-REC-E.164/)\nphone number.\nAutomatically generated using `phone` and `countryCode`, pending both values are valid.'
                        )
                        .optional()
                        .nullable(),
                      primary: z
                        .boolean()
                        .describe(
                          "Indicates whether this is the contact's primary phone number.\nWhen changing `primary` to `true` for a phone,\nthe contact's `primary` field for other phones becomes `false`.\nChanging the primary phone number also affects the subscription status to SMS messages that are decided based on the primary phone."
                        )
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(50)
                  .optional(),
              })
              .describe("Contact's phone numbers.")
              .optional(),
            addresses: z
              .object({
                items: z
                  .array(
                    z.object({
                      _id: z
                        .string()
                        .describe('Street address ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      tag: z
                        .enum([
                          'UNTAGGED',
                          'HOME',
                          'WORK',
                          'BILLING',
                          'SHIPPING',
                        ])
                        .describe(
                          'Address type.\n`UNTAGGED` is shown as "Other" in the Contact List.'
                        )
                        .optional(),
                      address: z
                        .object({
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine1: z.string().optional().nullable(),
                          addressLine2: z.string().optional().nullable(),
                        })
                        .describe('Street address.')
                        .optional(),
                    })
                  )
                  .max(50)
                  .optional(),
              })
              .describe("Contact's street addresses.")
              .optional(),
            company: z
              .string()
              .describe("Contact's company name.")
              .max(1000)
              .optional()
              .nullable(),
            jobTitle: z
              .string()
              .describe("Contact's job title.")
              .max(1000)
              .optional()
              .nullable(),
            birthdate: z
              .string()
              .describe(
                'Birth date in `YYYY-MM-DD` format. For example, `2020-03-15`.'
              )
              .optional()
              .nullable(),
            locale: z
              .string()
              .describe(
                'Locale in\n[IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nTypically, this is a lowercase 2-letter language code,\nfollowed by a hyphen, followed by an uppercase 2-letter country code.\nFor example, `en-US` for U.S. English, and `de-DE` for Germany German.'
              )
              .optional()
              .nullable(),
            labelKeys: z
              .object({ items: z.array(z.string()).max(2000).optional() })
              .describe(
                "List of contact's labels.\n\nLabels are used to organize contacts. Labels can be\nadded and removed using Label Contact and Unlabel Contact, respectively.\n\nTo view or manage contact labels, use the Labels API."
              )
              .optional(),
            extendedFields: z
              .object({
                items: z
                  .record(z.string(), z.any())
                  .describe(
                    "Contact's extended fields,\nwhere each key is the field key,\nand each value is the field's value for the contact.\n\nTo view and manage extended fields, use the Extended Fields API."
                  )
                  .optional(),
              })
              .describe(
                'Additional custom fields.\n\nEmpty fields are not returned.'
              )
              .optional(),
            picture: z
              .object({
                image: z
                  .string()
                  .describe(
                    'Image.\nThis can contain an image URL or a Wix Media image ID.'
                  )
                  .optional(),
              })
              .describe("Contact's profile picture.")
              .optional(),
          })
          .describe("Contact's details.")
          .optional(),
        primaryEmail: z
          .object({
            email: z
              .string()
              .describe(
                'Primary email address.\n\nThis field reflects the email address in `info.emails`\nwhere `primary` is `true`.'
              )
              .email()
              .optional()
              .nullable(),
            subscriptionStatus: z
              .enum([
                'NO_SUBSCRIPTION_STATUS',
                'NOT_SET',
                'PENDING',
                'SUBSCRIBED',
                'UNSUBSCRIBED',
              ])
              .describe(
                "Indicates the recipient's opt-in or opt-out status for marketing emails.\n\nDefault: `NOT_SET`"
              )
              .optional(),
            deliverabilityStatus: z
              .enum([
                'NOT_SET',
                'VALID',
                'BOUNCED',
                'SPAM_COMPLAINT',
                'INACTIVE',
              ])
              .describe(
                'Indicates last reported status of sent emails.\n\nDefault: `NOT_SET`'
              )
              .optional(),
          })
          .describe("Contact's primary email details.")
          .optional(),
        primaryPhone: z
          .object({
            countryCode: z
              .string()
              .describe(
                'Country code of the primary phone number in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
              )
              .optional()
              .nullable(),
            e164Phone: z
              .string()
              .describe(
                'Phone number in [ITU E.164](https://www.itu.int/rec/T-REC-E.164/) format.\nAutomatically generated using `phone` and `countryCode` when both values are valid.'
              )
              .optional()
              .nullable(),
            formattedPhone: z
              .string()
              .describe(
                'Formatted phone. Automatically generated using `phone` and `countryCode`.'
              )
              .optional()
              .nullable(),
            subscriptionStatus: z
              .enum([
                'NO_SUBSCRIPTION_STATUS',
                'NOT_SET',
                'PENDING',
                'SUBSCRIBED',
                'UNSUBSCRIBED',
              ])
              .describe(
                "Indicates the recipient's opt-in or opt-out status for SMS messages.\n\nDefault:\n- `NOT_SET` when the phone is a valid E164 phone.\n- `NO_SUBSCRIPTION_STATUS` when the phone is not a valid E164 phone."
              )
              .optional(),
            phone: z
              .string()
              .describe(
                'Primary phone number.\n\nThis field reflects the phone number in `info.phones`\nwhere `primary` is `true`.'
              )
              .optional()
              .nullable(),
          })
          .describe("Contact's primary phone details.")
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('The number of items returned in this response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('The offset which was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe('The total number of items that match the query.')
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          "A flag that indicates the server failed to calculate 'total' field."
        )
        .optional()
        .nullable(),
    })
    .describe('Details on the paged set of results returned.')
    .optional(),
});
export const QueryContactsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'info.locale': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'lastActivity.activityDate': z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'info.birthdate': z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'primaryInfo.email': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'info.name.first': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'info.name.last': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'info.company': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'info.jobTitle': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'primaryInfo.phone': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'info.labelKeys': z
            .object({ $hasAll: z.array(z.any()), $hasSome: z.array(z.any()) })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_createdDate',
                'lastActivity.activityDate',
                'info.birthdate',
                'primaryInfo.email',
                'info.name.first',
                'info.name.last',
                'info.company',
                'info.jobTitle',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
  options: z
    .object({
      search: z
        .string()
        .describe(
          'Plain text search for an exact match.\n\nSupported properties:\n`info.name.first`, `info.name.last`, `info.emails.email`, `info.phones.phone`\n\nMax: 100 characters'
        )
        .max(100)
        .optional()
        .nullable(),
    })
    .describe('Query contact options.')
    .optional(),
});
export const QueryContactsResponse = z.object({
  contacts: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Contact ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        revision: z
          .number()
          .int()
          .describe(
            'Revision number, which increments by 1 each time the contact is updated.\nTo prevent conflicting changes,\nthe existing `revision` must be specified when updating a contact.'
          )
          .optional(),
        source: z
          .object({
            sourceType: z
              .enum([
                'OTHER',
                'ADMIN',
                'WIX_APP',
                'IMPORT',
                'THIRD_PARTY',
                'WIX_BOOKINGS',
                'WIX_CHAT',
                'WIX_EMAIL_MARKETING',
                'WIX_EVENTS',
                'WIX_FORMS',
                'WIX_GROUPS',
                'WIX_HOTELS',
                'WIX_MARKET_PLACE',
                'WIX_MUSIC',
                'WIX_RESTAURANTS',
                'WIX_SITE_MEMBERS',
                'WIX_STORES',
                'WIX_CODE',
                'HOPP',
              ])
              .describe('Source type.')
              .optional(),
            appId: z
              .string()
              .describe('App ID, if the contact was created by an app.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
          .describe("Details about the contact's source.")
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time the contact was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the contact was last updated.')
          .optional()
          .nullable(),
        lastActivity: z
          .object({
            activityDate: z
              .date()
              .describe('Date and time of the last action.')
              .optional()
              .nullable(),
            activityType: z
              .enum([
                'GENERAL',
                'CONTACT_CREATED',
                'MEMBER_LOGIN',
                'MEMBER_REGISTER',
                'MEMBER_STATUS_CHANGED',
                'FORM_SUBMITTED',
                'INBOX_FORM_SUBMITTED',
                'INBOX_PAYMENT_REQUEST_PAID',
                'INBOX_MESSAGE_TO_CUSTOMER',
                'INBOX_MESSAGE_FROM_CUSTOMER',
                'NEWSLETTER_SUBSCRIPTION_FORM_SUBMITTED',
                'NEWSLETTER_SUBSCRIPTION_UNSUBSCRIBE',
                'ECOM_PURCHASE',
                'ECOM_CART_ABANDON',
                'ECOM_CHECKOUT_BUYER',
                'BOOKINGS_APPOINTMENT',
                'HOTELS_RESERVATION',
                'HOTELS_PURCHASE',
                'HOTELS_CONFIRMATION',
                'HOTELS_CANCEL',
                'VIDEO_PURCHASE',
                'VIDEO_RENT',
                'CASHIER_BUTTON_PURCHASE',
                'ARENA_NEW_LEAD',
                'EVENTS_RSVP',
                'INVOICE_PAY',
                'INVOICE_OVERDUE',
                'PRICE_QUOTE_ACCEPT',
                'PRICE_QUOTE_EXPIRE',
                'RESTAURANTS_ORDER',
                'RESTAURANTS_RESERVATION',
                'SHOUTOUT_OPEN',
                'SHOUTOUT_CLICK',
                'CONTACT_MERGED',
                'NEWSLETTER_SUBSCRIPTION_SUBSCRIBE',
                'NEWSLETTER_SUBSCRIPTION_PENDING',
                'NEWSLETTER_SUBSCRIPTION_NOT_SET',
                'PHONE_SUBSCRIPTION_SUBSCRIBE',
                'PHONE_SUBSCRIPTION_PENDING',
                'PHONE_SUBSCRIPTION_NOT_SET',
                'PHONE_SUBSCRIPTION_UNSUBSCRIBE',
              ])
              .describe("Contact's last action in the site.")
              .optional(),
          })
          .describe("Details about the contact's last action in the site.")
          .optional(),
        primaryInfo: z
          .object({
            email: z
              .string()
              .describe(
                'Primary email address.\n\nThis property reflects the email address in `info.emails`\nwhere `primary` is `true`.'
              )
              .email()
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe(
                'Primary phone number.\n\nThis property reflects the phone number in `info.phones`\nwhere `primary` is `true`.'
              )
              .optional()
              .nullable(),
          })
          .describe("Contact's primary phone and email.")
          .optional(),
        info: z
          .object({
            name: z
              .object({
                first: z
                  .string()
                  .describe("Contact's first name.")
                  .max(1000)
                  .optional()
                  .nullable(),
                last: z
                  .string()
                  .describe("Contact's last name.")
                  .max(1000)
                  .optional()
                  .nullable(),
              })
              .describe("Contact's first and last name.")
              .optional(),
            emails: z
              .object({
                items: z
                  .array(
                    z.object({
                      _id: z
                        .string()
                        .describe('Email ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      tag: z
                        .enum(['UNTAGGED', 'MAIN', 'HOME', 'WORK'])
                        .describe(
                          'Email type.\n\n`UNTAGGED` is shown as "Other" in the Contact List.'
                        )
                        .optional(),
                      email: z
                        .string()
                        .describe('Email address.')
                        .min(1)
                        .max(320)
                        .optional(),
                      primary: z
                        .boolean()
                        .describe(
                          "Indicates whether this is the contact's primary email address.\nWhen changing `primary` to `true` for an email,\nthe contact's other emails become `false`.\nChanging the primary email of a contact also affects the subscription status to marketing emails that are decided based on the primary email."
                        )
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(50)
                  .optional(),
              })
              .describe("Contact's email addresses.")
              .optional(),
            phones: z
              .object({
                items: z
                  .array(
                    z.object({
                      _id: z
                        .string()
                        .describe('Phone ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      tag: z
                        .enum([
                          'UNTAGGED',
                          'MAIN',
                          'HOME',
                          'MOBILE',
                          'WORK',
                          'FAX',
                        ])
                        .describe(
                          'Phone type.\n\n`UNTAGGED` is shown as "Other" in the Contact List.'
                        )
                        .optional(),
                      countryCode: z
                        .string()
                        .describe(
                          '[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) country code.'
                        )
                        .optional()
                        .nullable(),
                      phone: z
                        .string()
                        .describe('Phone number.')
                        .min(1)
                        .max(50)
                        .optional(),
                      e164Phone: z
                        .string()
                        .describe(
                          '[ITU E.164-formatted](https://www.itu.int/rec/T-REC-E.164/)\nphone number.\nAutomatically generated using `phone` and `countryCode`, pending both values are valid.'
                        )
                        .optional()
                        .nullable(),
                      primary: z
                        .boolean()
                        .describe(
                          "Indicates whether this is the contact's primary phone number.\nWhen changing `primary` to `true` for a phone,\nthe contact's `primary` field for other phones becomes `false`.\nChanging the primary phone number also affects the subscription status to SMS messages that are decided based on the primary phone."
                        )
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(50)
                  .optional(),
              })
              .describe("Contact's phone numbers.")
              .optional(),
            addresses: z
              .object({
                items: z
                  .array(
                    z.object({
                      _id: z
                        .string()
                        .describe('Street address ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      tag: z
                        .enum([
                          'UNTAGGED',
                          'HOME',
                          'WORK',
                          'BILLING',
                          'SHIPPING',
                        ])
                        .describe(
                          'Address type.\n`UNTAGGED` is shown as "Other" in the Contact List.'
                        )
                        .optional(),
                      address: z
                        .object({
                          city: z.string().optional().nullable(),
                          subdivision: z.string().optional().nullable(),
                          country: z.string().optional().nullable(),
                          postalCode: z.string().optional().nullable(),
                          addressLine1: z.string().optional().nullable(),
                          addressLine2: z.string().optional().nullable(),
                        })
                        .describe('Street address.')
                        .optional(),
                    })
                  )
                  .max(50)
                  .optional(),
              })
              .describe("Contact's street addresses.")
              .optional(),
            company: z
              .string()
              .describe("Contact's company name.")
              .max(1000)
              .optional()
              .nullable(),
            jobTitle: z
              .string()
              .describe("Contact's job title.")
              .max(1000)
              .optional()
              .nullable(),
            birthdate: z
              .string()
              .describe(
                'Birth date in `YYYY-MM-DD` format. For example, `2020-03-15`.'
              )
              .optional()
              .nullable(),
            locale: z
              .string()
              .describe(
                'Locale in\n[IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nTypically, this is a lowercase 2-letter language code,\nfollowed by a hyphen, followed by an uppercase 2-letter country code.\nFor example, `en-US` for U.S. English, and `de-DE` for Germany German.'
              )
              .optional()
              .nullable(),
            labelKeys: z
              .object({ items: z.array(z.string()).max(2000).optional() })
              .describe(
                "List of contact's labels.\n\nLabels are used to organize contacts. Labels can be\nadded and removed using Label Contact and Unlabel Contact, respectively.\n\nTo view or manage contact labels, use the Labels API."
              )
              .optional(),
            extendedFields: z
              .object({
                items: z
                  .record(z.string(), z.any())
                  .describe(
                    "Contact's extended fields,\nwhere each key is the field key,\nand each value is the field's value for the contact.\n\nTo view and manage extended fields, use the Extended Fields API."
                  )
                  .optional(),
              })
              .describe(
                'Additional custom fields.\n\nEmpty fields are not returned.'
              )
              .optional(),
            picture: z
              .object({
                image: z
                  .string()
                  .describe(
                    'Image.\nThis can contain an image URL or a Wix Media image ID.'
                  )
                  .optional(),
              })
              .describe("Contact's profile picture.")
              .optional(),
          })
          .describe("Contact's details.")
          .optional(),
        primaryEmail: z
          .object({
            email: z
              .string()
              .describe(
                'Primary email address.\n\nThis field reflects the email address in `info.emails`\nwhere `primary` is `true`.'
              )
              .email()
              .optional()
              .nullable(),
            subscriptionStatus: z
              .enum([
                'NO_SUBSCRIPTION_STATUS',
                'NOT_SET',
                'PENDING',
                'SUBSCRIBED',
                'UNSUBSCRIBED',
              ])
              .describe(
                "Indicates the recipient's opt-in or opt-out status for marketing emails.\n\nDefault: `NOT_SET`"
              )
              .optional(),
            deliverabilityStatus: z
              .enum([
                'NOT_SET',
                'VALID',
                'BOUNCED',
                'SPAM_COMPLAINT',
                'INACTIVE',
              ])
              .describe(
                'Indicates last reported status of sent emails.\n\nDefault: `NOT_SET`'
              )
              .optional(),
          })
          .describe("Contact's primary email details.")
          .optional(),
        primaryPhone: z
          .object({
            countryCode: z
              .string()
              .describe(
                'Country code of the primary phone number in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
              )
              .optional()
              .nullable(),
            e164Phone: z
              .string()
              .describe(
                'Phone number in [ITU E.164](https://www.itu.int/rec/T-REC-E.164/) format.\nAutomatically generated using `phone` and `countryCode` when both values are valid.'
              )
              .optional()
              .nullable(),
            formattedPhone: z
              .string()
              .describe(
                'Formatted phone. Automatically generated using `phone` and `countryCode`.'
              )
              .optional()
              .nullable(),
            subscriptionStatus: z
              .enum([
                'NO_SUBSCRIPTION_STATUS',
                'NOT_SET',
                'PENDING',
                'SUBSCRIBED',
                'UNSUBSCRIBED',
              ])
              .describe(
                "Indicates the recipient's opt-in or opt-out status for SMS messages.\n\nDefault:\n- `NOT_SET` when the phone is a valid E164 phone.\n- `NO_SUBSCRIPTION_STATUS` when the phone is not a valid E164 phone."
              )
              .optional(),
            phone: z
              .string()
              .describe(
                'Primary phone number.\n\nThis field reflects the phone number in `info.phones`\nwhere `primary` is `true`.'
              )
              .optional()
              .nullable(),
          })
          .describe("Contact's primary phone details.")
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('The number of items returned in this response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('The offset which was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe('The total number of items that match the query.')
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          "A flag that indicates the server failed to calculate 'total' field."
        )
        .optional()
        .nullable(),
    })
    .describe('Details on the paged set of results returned.')
    .optional(),
});
export const ListFacetsRequest = z.object({
  options: z
    .object({
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe(
              'Number of items to return. Learn more about pagination in [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#paging).\n\nDefault: `50`. <br>\nMaximum: `1000`.'
            )
            .min(0)
            .max(1000)
            .optional()
            .nullable(),
          offset: z
            .number()
            .int()
            .describe('Number of items to skip in the current sort order.')
            .min(0)
            .optional()
            .nullable(),
        })
        .describe('Pagination options.')
        .optional(),
      language: z
        .string()
        .describe('Language for localization.')
        .optional()
        .nullable(),
    })
    .describe('List facets options.')
    .optional(),
});
export const ListFacetsResponse = z.object({
  facets: z
    .array(
      z.object({
        facetType: z
          .enum([
            'UNKNOWN',
            'ALL_CONTACTS',
            'NOT_LABELED',
            'LABEL',
            'SUBSCRIPTION_STATUS',
            'MEMBERSHIP_STATUS',
          ])
          .describe('Type of facet.')
          .optional(),
        namespace: z
          .string()
          .describe(
            'Scope for facet keys.\nWhen a facet key exists in a namespace,\neach key is unique within that namespace.\n\nCurrently, facets not created by Wix are in the `custom` namespace.'
          )
          .optional()
          .nullable(),
        namespaceDisplayName: z
          .string()
          .describe('Human-readable display name.')
          .optional()
          .nullable(),
        facetKey: z
          .string()
          .describe('Facet key, automatically generated.')
          .optional()
          .nullable(),
        facetDisplayName: z
          .string()
          .describe('Human-readable name. Shown in the Wix UI.')
          .optional()
          .nullable(),
        count: z
          .number()
          .int()
          .describe('Number of contacts the facet applies to.')
          .optional()
          .nullable(),
        queryFilter: z
          .record(z.string(), z.any())
          .describe('Filter used to query contacts of this facet.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('The number of items returned in this response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('The offset which was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe('The total number of items that match the query.')
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          "A flag that indicates the server failed to calculate 'total' field."
        )
        .optional()
        .nullable(),
    })
    .describe('Details on the paged set of results returned.')
    .optional(),
});
export const QueryFacetsRequest = z.object({
  options: z
    .object({
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe(
              'Number of items to return. Learn more about pagination in [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#paging).\n\nDefault: `50`. <br>\nMaximum: `1000`.'
            )
            .min(0)
            .max(1000)
            .optional()
            .nullable(),
          offset: z
            .number()
            .int()
            .describe('Number of items to skip in the current sort order.')
            .min(0)
            .optional()
            .nullable(),
        })
        .describe('Pagination options.')
        .optional(),
      language: z
        .string()
        .describe('Language for localization.')
        .optional()
        .nullable(),
      filter: z
        .record(z.string(), z.any())
        .describe(
          "Filterable fields:\n- `'info.extendedFields.emailSubscriptions.effectiveEmail'` (`$exists`).\n- `'info.extendedFields.emailSubscriptions.subscriptionStatus'` (`$eq`, `$ne`, `$in`, `$nin`).\n- `'info.extendedFields.emailSubscriptions.deliverabilityStatus'` (`$eq`, `$ne`, `$in`, `$nin`)."
        )
        .optional()
        .nullable(),
    })
    .describe('Query facets options.')
    .optional(),
});
export const QueryFacetsResponse = z.object({
  facets: z
    .array(
      z.object({
        facetType: z
          .enum([
            'UNKNOWN',
            'ALL_CONTACTS',
            'NOT_LABELED',
            'LABEL',
            'SUBSCRIPTION_STATUS',
            'MEMBERSHIP_STATUS',
          ])
          .describe('Type of facet.')
          .optional(),
        namespace: z
          .string()
          .describe(
            'Scope for facet keys.\nWhen a facet key exists in a namespace,\neach key is unique within that namespace.\n\nCurrently, facets not created by Wix are in the `custom` namespace.'
          )
          .optional()
          .nullable(),
        namespaceDisplayName: z
          .string()
          .describe('Human-readable display name.')
          .optional()
          .nullable(),
        facetKey: z
          .string()
          .describe('Facet key, automatically generated.')
          .optional()
          .nullable(),
        facetDisplayName: z
          .string()
          .describe('Human-readable name. Shown in the Wix UI.')
          .optional()
          .nullable(),
        count: z
          .number()
          .int()
          .describe('Number of contacts the facet applies to.')
          .optional()
          .nullable(),
        queryFilter: z
          .record(z.string(), z.any())
          .describe('Filter used to query contacts of this facet.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('The number of items returned in this response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('The offset which was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe('The total number of items that match the query.')
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          "A flag that indicates the server failed to calculate 'total' field."
        )
        .optional()
        .nullable(),
    })
    .describe('Details on the paged set of results returned.')
    .optional(),
});
export const BulkDeleteContactsRequest = z.object({
  options: z
    .object({
      filter: z
        .record(z.string(), z.any())
        .describe(
          'Filter object.\n\nPossible filters:\n`$eq`, `$exists`, `$gt`, `$gte`, `$hasAll`, `$hasSome`, `$in`, `$lt`, `$lte`, `$ne`, `$startsWith`.\n\nSee [Sort, Filter, and Search](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/sort-filter-and-search)\nfor a detailed list of supported filters for contact properties, extended fields, and custom fields.\n\n\nExample:\n`{ "filter": { "info.name.last": "Smith" } }`'
        )
        .optional()
        .nullable(),
      search: z
        .string()
        .describe(
          'Plain text search for an exact match, up to 100 characters.\n\nSearchable fields:\n\n- `info.name.first`\n- `info.name.last`\n- `info.emails.email`\n- `info.phones.phone`'
        )
        .max(100)
        .optional()
        .nullable(),
    })
    .describe('Bulk delete contacts options.')
    .optional(),
});
export const BulkDeleteContactsResponse = z.object({
  jobId: z
    .string()
    .describe(
      "Bulk job ID.\nThe job's status can be retrieved with Get Bulk Job."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const BulkUpdateContactsRequest = z.object({
  options: z
    .object({
      filter: z
        .record(z.string(), z.any())
        .describe(
          'Filter object.\n\nPossible filters:\n`$eq`, `$exists`, `$gt`, `$gte`, `$hasAll`, `$hasSome`, `$in`, `$lt`, `$lte`, `$ne`, `$startsWith`.\n\nSee [Sort, Filter, and Search](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/sort-filter-and-search)\nfor a detailed list of supported filters for contact properties, extended fields, and custom fields.\n\n\nExample:\n`{ "filter": { "info.name.last": "Smith" } }`'
        )
        .optional()
        .nullable(),
      search: z
        .string()
        .describe(
          'Plain text search for an exact match, up to 100 characters.\n\nSearchable fields:\n\n- `info.name.first`\n- `info.name.last`\n- `info.emails.email`\n- `info.phones.phone`'
        )
        .max(100)
        .optional()
        .nullable(),
      info: z
        .object({
          name: z
            .object({
              first: z
                .string()
                .describe("Contact's first name.")
                .max(1000)
                .optional()
                .nullable(),
              last: z
                .string()
                .describe("Contact's last name.")
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe("Contact's first and last name.")
            .optional(),
          emails: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Email ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum(['UNTAGGED', 'MAIN', 'HOME', 'WORK'])
                      .optional(),
                    email: z
                      .string()
                      .describe('Email address.')
                      .min(1)
                      .max(320)
                      .optional(),
                    primary: z
                      .boolean()
                      .describe(
                        "Indicates whether this is the contact's primary email address.\nWhen changing `primary` to `true` for an email,\nthe contact's other emails become `false`.\nChanging the primary email of a contact also affects the subscription status to marketing emails that are decided based on the primary email."
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's email addresses.")
            .optional(),
          phones: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Phone ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum([
                        'UNTAGGED',
                        'MAIN',
                        'HOME',
                        'MOBILE',
                        'WORK',
                        'FAX',
                      ])
                      .optional(),
                    countryCode: z
                      .string()
                      .describe(
                        '[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) country code.'
                      )
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Phone number.')
                      .min(1)
                      .max(50)
                      .optional(),
                    e164Phone: z
                      .string()
                      .describe(
                        '[ITU E.164-formatted](https://www.itu.int/rec/T-REC-E.164/)\nphone number.\nAutomatically generated using `phone` and `countryCode`, pending both values are valid.'
                      )
                      .optional()
                      .nullable(),
                    primary: z
                      .boolean()
                      .describe(
                        "Indicates whether this is the contact's primary phone number.\nWhen changing `primary` to `true` for a phone,\nthe contact's `primary` field for other phones becomes `false`.\nChanging the primary phone number also affects the subscription status to SMS messages that are decided based on the primary phone."
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's phone numbers.")
            .optional(),
          addresses: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Street address ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum(['UNTAGGED', 'HOME', 'WORK', 'BILLING', 'SHIPPING'])
                      .optional(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                        addressLine2: z.string().optional().nullable(),
                      })
                      .describe('Street address.')
                      .optional(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's street addresses.")
            .optional(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(1000)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(1000)
            .optional()
            .nullable(),
          birthdate: z
            .string()
            .describe(
              'Birth date in `YYYY-MM-DD` format. For example, `2020-03-15`.'
            )
            .optional()
            .nullable(),
          locale: z
            .string()
            .describe(
              'Locale in\n[IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nTypically, this is a lowercase 2-letter language code,\nfollowed by a hyphen, followed by an uppercase 2-letter country code.\nFor example, `en-US` for U.S. English, and `de-DE` for Germany German.'
            )
            .optional()
            .nullable(),
          labelKeys: z
            .object({ items: z.array(z.string()).max(2000).optional() })
            .describe(
              "List of contact's labels.\n\nLabels are used to organize contacts. Labels can be\nadded and removed using Label Contact and Unlabel Contact, respectively.\n\nTo view or manage contact labels, use the Labels API."
            )
            .optional(),
          extendedFields: z
            .object({
              items: z
                .record(z.string(), z.any())
                .describe(
                  "Contact's extended fields,\nwhere each key is the field key,\nand each value is the field's value for the contact.\n\nTo view and manage extended fields, use the Extended Fields API."
                )
                .optional(),
            })
            .describe(
              'Additional custom fields.\n\nEmpty fields are not returned.'
            )
            .optional(),
          picture: z
            .object({
              image: z
                .string()
                .describe(
                  'Image.\nThis can contain an image URL or a Wix Media image ID.'
                )
                .optional(),
            })
            .describe("Contact's profile picture.")
            .optional(),
        })
        .describe('Contact info.')
        .optional(),
    })
    .describe('Bulk update contacts options.')
    .optional(),
});
export const BulkUpdateContactsResponse = z.object({
  jobId: z
    .string()
    .describe(
      "Bulk job ID.\nThe job's status can be retrieved with Get Bulk Job."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const BulkLabelAndUnlabelContactsRequest = z.object({
  options: z
    .object({
      filter: z
        .record(z.string(), z.any())
        .describe(
          'Filter options.\nLabels will be removed from contacts that meet the `filter` and `search` criteria.\n\nSee\n[Field Support for Filtering, Sorting, and Searching](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/sort-filter-and-search)\nfor a list of supported filters and fields.'
        )
        .optional()
        .nullable(),
      search: z
        .string()
        .describe(
          'Plain text search for an exact match, up to 100 characters.\nLabels will be removed from contacts that meet the `filter` and `search` criteria.\n\nSee\n[Field Support for Filtering, Sorting, and Searching](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/sort-filter-and-search)\nfor a list of searchable fields.'
        )
        .max(100)
        .optional()
        .nullable(),
      labelKeysToAdd: z.array(z.string()).max(100).optional(),
      labelKeysToRemove: z.array(z.string()).max(100).optional(),
    })
    .describe('Bulk label and unlabel contacts options.')
    .optional(),
});
export const BulkLabelAndUnlabelContactsResponse = z.object({
  jobId: z
    .string()
    .describe(
      "Bulk job ID.\nThe job's status can be retrieved with Get Bulk Job."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const GetContactRequest = z.object({
  _id: z
    .string()
    .describe('ID of the contact to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fields: z.array(z.string()).max(100).optional(),
      fieldsets: z
        .array(z.enum(['BASIC', 'COMMUNICATION_DETAILS', 'EXTENDED', 'FULL']))
        .max(10)
        .optional(),
    })
    .describe('Get contact options.')
    .optional(),
});
export const GetContactResponse = z.object({
  _id: z
    .string()
    .describe('Contact ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  revision: z
    .number()
    .int()
    .describe(
      'Revision number, which increments by 1 each time the contact is updated.\nTo prevent conflicting changes,\nthe existing `revision` must be specified when updating a contact.'
    )
    .optional(),
  source: z
    .object({
      sourceType: z
        .enum([
          'OTHER',
          'ADMIN',
          'WIX_APP',
          'IMPORT',
          'THIRD_PARTY',
          'WIX_BOOKINGS',
          'WIX_CHAT',
          'WIX_EMAIL_MARKETING',
          'WIX_EVENTS',
          'WIX_FORMS',
          'WIX_GROUPS',
          'WIX_HOTELS',
          'WIX_MARKET_PLACE',
          'WIX_MUSIC',
          'WIX_RESTAURANTS',
          'WIX_SITE_MEMBERS',
          'WIX_STORES',
          'WIX_CODE',
          'HOPP',
        ])
        .describe('Source type.')
        .optional(),
      appId: z
        .string()
        .describe('App ID, if the contact was created by an app.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .describe("Details about the contact's source.")
    .optional(),
  _createdDate: z
    .date()
    .describe('Date and time the contact was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the contact was last updated.')
    .optional()
    .nullable(),
  lastActivity: z
    .object({
      activityDate: z
        .date()
        .describe('Date and time of the last action.')
        .optional()
        .nullable(),
      activityType: z
        .enum([
          'GENERAL',
          'CONTACT_CREATED',
          'MEMBER_LOGIN',
          'MEMBER_REGISTER',
          'MEMBER_STATUS_CHANGED',
          'FORM_SUBMITTED',
          'INBOX_FORM_SUBMITTED',
          'INBOX_PAYMENT_REQUEST_PAID',
          'INBOX_MESSAGE_TO_CUSTOMER',
          'INBOX_MESSAGE_FROM_CUSTOMER',
          'NEWSLETTER_SUBSCRIPTION_FORM_SUBMITTED',
          'NEWSLETTER_SUBSCRIPTION_UNSUBSCRIBE',
          'ECOM_PURCHASE',
          'ECOM_CART_ABANDON',
          'ECOM_CHECKOUT_BUYER',
          'BOOKINGS_APPOINTMENT',
          'HOTELS_RESERVATION',
          'HOTELS_PURCHASE',
          'HOTELS_CONFIRMATION',
          'HOTELS_CANCEL',
          'VIDEO_PURCHASE',
          'VIDEO_RENT',
          'CASHIER_BUTTON_PURCHASE',
          'ARENA_NEW_LEAD',
          'EVENTS_RSVP',
          'INVOICE_PAY',
          'INVOICE_OVERDUE',
          'PRICE_QUOTE_ACCEPT',
          'PRICE_QUOTE_EXPIRE',
          'RESTAURANTS_ORDER',
          'RESTAURANTS_RESERVATION',
          'SHOUTOUT_OPEN',
          'SHOUTOUT_CLICK',
          'CONTACT_MERGED',
          'NEWSLETTER_SUBSCRIPTION_SUBSCRIBE',
          'NEWSLETTER_SUBSCRIPTION_PENDING',
          'NEWSLETTER_SUBSCRIPTION_NOT_SET',
          'PHONE_SUBSCRIPTION_SUBSCRIBE',
          'PHONE_SUBSCRIPTION_PENDING',
          'PHONE_SUBSCRIPTION_NOT_SET',
          'PHONE_SUBSCRIPTION_UNSUBSCRIBE',
        ])
        .describe("Contact's last action in the site.")
        .optional(),
    })
    .describe("Details about the contact's last action in the site.")
    .optional(),
  primaryInfo: z
    .object({
      email: z
        .string()
        .describe(
          'Primary email address.\n\nThis property reflects the email address in `info.emails`\nwhere `primary` is `true`.'
        )
        .email()
        .optional()
        .nullable(),
      phone: z
        .string()
        .describe(
          'Primary phone number.\n\nThis property reflects the phone number in `info.phones`\nwhere `primary` is `true`.'
        )
        .optional()
        .nullable(),
    })
    .describe("Contact's primary phone and email.")
    .optional(),
  info: z
    .object({
      name: z
        .object({
          first: z
            .string()
            .describe("Contact's first name.")
            .max(1000)
            .optional()
            .nullable(),
          last: z
            .string()
            .describe("Contact's last name.")
            .max(1000)
            .optional()
            .nullable(),
        })
        .describe("Contact's first and last name.")
        .optional(),
      emails: z
        .object({
          items: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Email ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                tag: z
                  .enum(['UNTAGGED', 'MAIN', 'HOME', 'WORK'])
                  .describe(
                    'Email type.\n\n`UNTAGGED` is shown as "Other" in the Contact List.'
                  )
                  .optional(),
                email: z
                  .string()
                  .describe('Email address.')
                  .min(1)
                  .max(320)
                  .optional(),
                primary: z
                  .boolean()
                  .describe(
                    "Indicates whether this is the contact's primary email address.\nWhen changing `primary` to `true` for an email,\nthe contact's other emails become `false`.\nChanging the primary email of a contact also affects the subscription status to marketing emails that are decided based on the primary email."
                  )
                  .optional()
                  .nullable(),
              })
            )
            .max(50)
            .optional(),
        })
        .describe("Contact's email addresses.")
        .optional(),
      phones: z
        .object({
          items: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Phone ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                tag: z
                  .enum(['UNTAGGED', 'MAIN', 'HOME', 'MOBILE', 'WORK', 'FAX'])
                  .describe(
                    'Phone type.\n\n`UNTAGGED` is shown as "Other" in the Contact List.'
                  )
                  .optional(),
                countryCode: z
                  .string()
                  .describe(
                    '[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) country code.'
                  )
                  .optional()
                  .nullable(),
                phone: z
                  .string()
                  .describe('Phone number.')
                  .min(1)
                  .max(50)
                  .optional(),
                e164Phone: z
                  .string()
                  .describe(
                    '[ITU E.164-formatted](https://www.itu.int/rec/T-REC-E.164/)\nphone number.\nAutomatically generated using `phone` and `countryCode`, pending both values are valid.'
                  )
                  .optional()
                  .nullable(),
                primary: z
                  .boolean()
                  .describe(
                    "Indicates whether this is the contact's primary phone number.\nWhen changing `primary` to `true` for a phone,\nthe contact's `primary` field for other phones becomes `false`.\nChanging the primary phone number also affects the subscription status to SMS messages that are decided based on the primary phone."
                  )
                  .optional()
                  .nullable(),
              })
            )
            .max(50)
            .optional(),
        })
        .describe("Contact's phone numbers.")
        .optional(),
      addresses: z
        .object({
          items: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Street address ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                tag: z
                  .enum(['UNTAGGED', 'HOME', 'WORK', 'BILLING', 'SHIPPING'])
                  .describe(
                    'Address type.\n`UNTAGGED` is shown as "Other" in the Contact List.'
                  )
                  .optional(),
                address: z
                  .object({
                    city: z.string().optional().nullable(),
                    subdivision: z.string().optional().nullable(),
                    country: z.string().optional().nullable(),
                    postalCode: z.string().optional().nullable(),
                    addressLine1: z.string().optional().nullable(),
                    addressLine2: z.string().optional().nullable(),
                  })
                  .describe('Street address.')
                  .optional(),
              })
            )
            .max(50)
            .optional(),
        })
        .describe("Contact's street addresses.")
        .optional(),
      company: z
        .string()
        .describe("Contact's company name.")
        .max(1000)
        .optional()
        .nullable(),
      jobTitle: z
        .string()
        .describe("Contact's job title.")
        .max(1000)
        .optional()
        .nullable(),
      birthdate: z
        .string()
        .describe(
          'Birth date in `YYYY-MM-DD` format. For example, `2020-03-15`.'
        )
        .optional()
        .nullable(),
      locale: z
        .string()
        .describe(
          'Locale in\n[IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nTypically, this is a lowercase 2-letter language code,\nfollowed by a hyphen, followed by an uppercase 2-letter country code.\nFor example, `en-US` for U.S. English, and `de-DE` for Germany German.'
        )
        .optional()
        .nullable(),
      labelKeys: z
        .object({ items: z.array(z.string()).max(2000).optional() })
        .describe(
          "List of contact's labels.\n\nLabels are used to organize contacts. Labels can be\nadded and removed using Label Contact and Unlabel Contact, respectively.\n\nTo view or manage contact labels, use the Labels API."
        )
        .optional(),
      extendedFields: z
        .object({
          items: z
            .record(z.string(), z.any())
            .describe(
              "Contact's extended fields,\nwhere each key is the field key,\nand each value is the field's value for the contact.\n\nTo view and manage extended fields, use the Extended Fields API."
            )
            .optional(),
        })
        .describe('Additional custom fields.\n\nEmpty fields are not returned.')
        .optional(),
      picture: z
        .object({
          image: z
            .string()
            .describe(
              'Image.\nThis can contain an image URL or a Wix Media image ID.'
            )
            .optional(),
        })
        .describe("Contact's profile picture.")
        .optional(),
    })
    .describe("Contact's details.")
    .optional(),
  primaryEmail: z
    .object({
      email: z
        .string()
        .describe(
          'Primary email address.\n\nThis field reflects the email address in `info.emails`\nwhere `primary` is `true`.'
        )
        .email()
        .optional()
        .nullable(),
      subscriptionStatus: z
        .enum([
          'NO_SUBSCRIPTION_STATUS',
          'NOT_SET',
          'PENDING',
          'SUBSCRIBED',
          'UNSUBSCRIBED',
        ])
        .describe(
          "Indicates the recipient's opt-in or opt-out status for marketing emails.\n\nDefault: `NOT_SET`"
        )
        .optional(),
      deliverabilityStatus: z
        .enum(['NOT_SET', 'VALID', 'BOUNCED', 'SPAM_COMPLAINT', 'INACTIVE'])
        .describe(
          'Indicates last reported status of sent emails.\n\nDefault: `NOT_SET`'
        )
        .optional(),
    })
    .describe("Contact's primary email details.")
    .optional(),
  primaryPhone: z
    .object({
      countryCode: z
        .string()
        .describe(
          'Country code of the primary phone number in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
        )
        .optional()
        .nullable(),
      e164Phone: z
        .string()
        .describe(
          'Phone number in [ITU E.164](https://www.itu.int/rec/T-REC-E.164/) format.\nAutomatically generated using `phone` and `countryCode` when both values are valid.'
        )
        .optional()
        .nullable(),
      formattedPhone: z
        .string()
        .describe(
          'Formatted phone. Automatically generated using `phone` and `countryCode`.'
        )
        .optional()
        .nullable(),
      subscriptionStatus: z
        .enum([
          'NO_SUBSCRIPTION_STATUS',
          'NOT_SET',
          'PENDING',
          'SUBSCRIBED',
          'UNSUBSCRIBED',
        ])
        .describe(
          "Indicates the recipient's opt-in or opt-out status for SMS messages.\n\nDefault:\n- `NOT_SET` when the phone is a valid E164 phone.\n- `NO_SUBSCRIPTION_STATUS` when the phone is not a valid E164 phone."
        )
        .optional(),
      phone: z
        .string()
        .describe(
          'Primary phone number.\n\nThis field reflects the phone number in `info.phones`\nwhere `primary` is `true`.'
        )
        .optional()
        .nullable(),
    })
    .describe("Contact's primary phone details.")
    .optional(),
});
